'use client'

import useEmblaCarousel from 'embla-carousel-react'
import { motion } from 'framer-motion'
import { Img } from 'openimg/react'
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router'
import { InteractiveGradientBlob as InteractiveGradientBlobHome } from '#app/components/marketing/interactive-gradient-circle.tsx'
import { InteractiveGradientBlob } from '#app/components/ui/background-gradient-animation.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { InteractiveHoverButton } from '#app/components/ui/interactive-hover-button.tsx'
import {
	Floating,
	FloatingElement,
} from '#app/components/ui/parallax-floating.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useNavigationTheme } from '#app/utils/navigation-theme-context.tsx'
import {
	CONDENSED_FONT_STYLE,
	renderHighlightedTitle,
} from '#app/utils/text-rendering.tsx'
import { HoverVideoOverlay } from './hover-video-overlay.tsx'
import { IgniteVideoPlayer } from './ignite-video-player.tsx'
import { useLazyCases } from './use-lazy-cases.tsx'
import { VideoAsImage } from './video-as-image.tsx'

/**
 * Converts an image URL to use the /resources/images optimization endpoint.
 * - If already using /resources/images, returns as-is
 * - If it's a media library object key (media/...), uses objectKey parameter
 * - For external URLs or public folder paths, uses src parameter
 */
function getOptimizedImageUrl(url: string | null | undefined): string | null {
	if (!url) return null
	// Already using the optimization endpoint
	if (url.startsWith('/resources/images')) return url
	// Media library object keys (stored in S3/Tigris)
	if (url.startsWith('media/')) {
		return `/resources/images?objectKey=${encodeURIComponent(url)}`
	}
	// External URLs or local paths - wrap with src parameter
	return `/resources/images?src=${encodeURIComponent(url)}`
}

/**
 * Returns an optimized background image URL with explicit width and format.
 * CSS background-image can't use <picture>/srcset, so we request a specific
 * size and modern format to avoid serving full-resolution originals.
 */
/**
 * Computes width and height props for openimg <Img> based on max CSS display
 * width and aspect ratio. openimg caps srcSet at the width prop, so we need
 * width large enough to cover retina displays (2x by default).
 */
function getImgDimensions(
	maxCssWidth: number,
	aspectRatio: string,
	retina = 2,
): { width: number; height: number } {
	const width = maxCssWidth * retina
	const ratioMap: Record<string, number> = {
		'21:9': 21 / 9,
		'16:9': 16 / 9,
		'2:1': 2,
		'3:2': 3 / 2,
		'4:3': 4 / 3,
		'1:1': 1,
		'3:4': 3 / 4,
		'2:3': 2 / 3,
		'9:16': 9 / 16,
	}
	const ratio = ratioMap[aspectRatio] || 4 / 3
	const height = Math.round(width / ratio)
	return { width, height }
}

function getOptimizedBgUrl(
	url: string | null | undefined,
	width = 1920,
): string | null {
	const base = getOptimizedImageUrl(url)
	if (!base) return null
	const separator = base.includes('?') ? '&' : '?'
	return `${base}${separator}w=${width}&format=webp`
}

// Utility function to determine if a color is dark
function isColorDark(hexColor: string): boolean {
	// Default to dark if no color provided
	if (!hexColor) return true

	// Remove # if present
	const hex = hexColor.replace('#', '')

	// Convert to RGB
	const r = parseInt(hex.substring(0, 2), 16)
	const g = parseInt(hex.substring(2, 4), 16)
	const b = parseInt(hex.substring(4, 6), 16)

	// Calculate relative luminance using sRGB
	// Formula from WCAG 2.1
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

	// If luminance is less than 0.5, the color is considered dark
	return luminance < 0.5
}

// Animation variants — ease-out-quad for smooth entrance without bounce
const easeOut = [0.25, 0.46, 0.45, 0.94] as const

const fadeInUp = {
	initial: { opacity: 0, y: 14 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.9, ease: easeOut },
}

const staggerContainer = {
	initial: {},
	animate: {
		transition: {
			staggerChildren: 0.15,
			delayChildren: 0.1,
		},
	},
}

const staggerItem = {
	initial: { opacity: 0, y: 14 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.9, ease: easeOut },
}

// Figma grid width constants — derived from "Ignite identity web grid" Figma designs (1512px viewport).
const GRID_NARROW = 'max-w-[940px]' // Hero, 1-col text, long text, services list
const GRID_WIDE = 'max-w-[1176px]' // 2-col text, split text
// Figma "hkopjes" heading classes — responsive sizes matching the design system.
// H1-H4: Condensed Black (900), H5: Bold (700). All use leading-none.
const HEADING_CLASSES = {
	h1: 'text-[40px] leading-none font-black md:text-[56px] lg:text-[76px]',
	h2: 'text-[36px] leading-none font-black md:text-[48px] lg:text-[64px]',
	h3: 'text-[28px] leading-none font-black md:text-[34px] lg:text-[40px]',
	h4: 'text-[24px] leading-none font-black md:text-[28px] lg:text-[32px]',
} as const

const RICH_TEXT_HEADING_CLASSES = [
	'[&_h1]:text-[40px] [&_h1]:leading-none [&_h1]:md:text-[56px] [&_h1]:lg:text-[76px]',
	'[&_h2]:text-[36px] [&_h2]:leading-none [&_h2]:md:text-[48px] [&_h2]:lg:text-[64px]',
	'[&_h3]:text-[28px] [&_h3]:leading-none [&_h3]:md:text-[34px] [&_h3]:lg:text-[40px]',
	'[&_h4]:text-[24px] [&_h4]:leading-none [&_h4]:md:text-[28px] [&_h4]:lg:text-[32px]',
].join(' ')

// Shared padding utility - maps padding levels (0-5) to Tailwind classes
// Padding levels: 0 = none, 1 = small, 2 = medium (default), 3 = large, 4 = xl, 5 = 2xl
function getPaddingClass(level: number, position: 'top' | 'bottom') {
	const prefix = position === 'top' ? 'pt' : 'pb'
	switch (level) {
		case 0:
			return `${prefix}-0`
		case 1:
			return `${prefix}-4 md:${prefix}-6`
		case 2:
			return `${prefix}-8 md:${prefix}-12`
		case 3:
			return `${prefix}-12 md:${prefix}-16`
		case 4:
			return `${prefix}-16 md:${prefix}-20`
		case 5:
			return `${prefix}-20 md:${prefix}-24`
		default:
			return `${prefix}-8 md:${prefix}-12`
	}
}

// Block content types
interface HeroBlockContent {
	title: string
	subtitle?: string // Subheading (bold intro text)
	description?: string // Longer description text
	highlightWords?: string // Comma-separated words to highlight with gradient
	companyName?: string // Client/company name (e.g., "Uithoflijn")
	serviceLinks?: Array<{ label: string; slug: string }> // Clickable service tags
	showAwardBadge?: boolean // Award winner symbol
	awardText?: string // Award text (e.g., "Award winner 2024")
	backgroundImage?: string
	backgroundColor?: string
	textColor?: string
	alignment?: 'left' | 'center' | 'right'
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
	showGradientBlob?: boolean // Show interactive gradient blob animation
	videoMode?: 'none' | 'hover' | 'autoplay'
	videoPlaybackId?: string
}

interface TextBlockContent {
	content: string
	columns?: number
	alignment?: 'left' | 'center' | 'right' // Text alignment
	lineHeight?: 'tight' | 'snug' | 'compact' | 'normal' | 'relaxed' | 'loose' // Line height (tight=0.85, snug=0.95)
	title?: string
	highlightWords?: string
	quote?: string // Optional quote displayed left of text (Helvetica Neue Condensed Black)
	showQuote?: boolean // Toggle to show/hide the quote
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
}

interface ImageGridBlockContent {
	images: Array<{
		url: string
		alt: string
		caption?: string
		mediaAssetId?: string
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
	}>
	columns?: number
	title?: string
	highlightWords?: string
	aspectRatio?:
		| 'auto'
		| '16:9'
		| '4:3'
		| '3:2'
		| '1:1'
		| '2:3'
		| '9:16'
		| '21:9' // Image aspect ratio with object-cover
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
}

interface CardCarouselBlockContent {
	title?: string
	highlightWords?: string
	cards: Array<{
		title: string
		description?: string
		image?: string
		mediaAssetId?: string
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
		link?: string
		icon?: string
		buttonText?: string
		items?: string[]
	}>
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
}

interface LongTextBlockContent {
	content: string
	title?: string
	highlightWords?: string
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
	backgroundColor?: string // Background color (hex)
}

interface ProjectCarouselBlockContent {
	title?: string
	highlightWords?: string
	showFeatured?: boolean
	caseStudies?: Array<{
		id: string
		title: string
		slug: string
		client?: string | null
		imageUrl?: string | null
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
	}>
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
}

interface CasesOverviewBlockContent {
	title?: string
	highlightWords?: string
	displayMode?: 'rows' | 'limit5' | 'unlimited'
	rows?: number
	showFilters?: boolean
	filterType?: 'service' | 'sector' | 'both'
	fixedServiceSlug?: string // Lock to a specific service
	fixedSectorSlug?: string // Lock to a specific sector
	fullWidthAspectRatio?: '21:9' | '16:9' | '4:3' | '3:2' | '2:1'
	twoColumnAspectRatio?: '4:3' | '3:2' | '16:9' | '1:1' | '3:4'
	mobileAspectRatio?: '4:3' | '3:2' | '1:1' | '3:4' | '9:16'
	caseStudies?: Array<{
		id: string
		title: string
		slug: string
		client?: string | null
		imageUrl?: string | null
		imageAlt?: string | null
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
		featured?: boolean
		order?: number
		services?: Array<{ id: string; name: string; slug: string }>
		sectors?: Array<{ id: string; name: string; slug: string }>
	}>
	services?: Array<{ id: string; name: string; slug: string }>
	sectors?: Array<{ id: string; name: string; slug: string }>
	currentServiceSlug?: string | null
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
}

interface SectionImageItem {
	url: string
	alt?: string
}

interface SectionImageBlockContent {
	url?: string // Legacy single image support
	alt?: string // Legacy single image support
	images?: SectionImageItem[] // New multiple images support
	caption?: string
	fullWidth?: boolean
	aspectRatio?:
		| 'auto'
		| '16:9'
		| '4:3'
		| '3:2'
		| '1:1'
		| '2:3'
		| '9:16'
		| '21:9' // Image aspect ratio with object-cover
	mediaAssetId?: string // Link back to MediaAsset
	videoMode?: 'none' | 'hover' | 'autoplay' // Video display mode
	videoPlaybackId?: string // Mux playback ID
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
}

interface VideoBlockContent {
	mediaAssetId?: string // Media library asset ID
	muxPlaybackId?: string // Mux playback ID for streaming
	videoUrl?: string // Legacy: Direct video URL fallback (backward compat)
	duration?: number // Video duration in seconds
	posterUrl?: string // Custom poster/thumbnail image from media library
	thumbnailTime?: number // Mux thumbnail timestamp in seconds
	title?: string
	caption?: string
	autoplay?: boolean
	loop?: boolean
	muted?: boolean
	fullWidth?: boolean
	aspectRatio?: '16:9' | '9:16' | '4:3' | '1:1' | '21:9' // Video aspect ratio
	paddingTop?: number // 0-5 levels of padding
	paddingBottom?: number // 0-5 levels of padding
}

interface HeroIndexBlockContent {
	title?: string // Full title text (new approach)
	highlightWords?: string // Comma-separated words to gradient highlight
	fontSizeMin?: number // Minimum font size in px (default 40)
	fontSizePreferred?: number // Preferred font size in vw (default 6)
	fontSizeMax?: number // Maximum font size in px (default 96)
	gradientText?: string // Legacy: text with gradient color
	lines?: string[] // Legacy: array of text lines
	tagline?: string // Subtext below the hero
	backgroundColor?: string
	showGradientBlob?: boolean
	blobContinuous?: boolean
	headerSize?: 'small' | 'medium' | 'large' | 'xlarge' // Legacy preset
	backgroundImage?: string // Background image URL
	noFlexiblePaddingMobile?: boolean // Keep fixed padding on mobile
	paddingTop?: number
	paddingBottom?: number
}

interface ContactHeroBlockContent {
	headlineLines?: string[] // Multi-line italic headline
	tagline?: string // Left column tagline
	subtagline?: string // Left column subtagline
	companyName?: string
	streetAddress?: string
	postalCode?: string
	city?: string
	country?: string
	telephone?: string // Formatted display
	telephoneRaw?: string // E.164 format for tel: link
	email?: string
	emailLabel?: string // Button label
	backgroundColor?: string
	textColor?: string
	paddingTop?: number
	paddingBottom?: number
}

interface QuoteCarouselBlockContent {
	quotes?: Array<{
		id: string
		text: string
		author: string
		company?: string
		image?: string
	}>
	autoplayDelay?: number
	backgroundColor?: string
	paddingTop?: number
	paddingBottom?: number
}

interface FloatingImagesBlockContent {
	images?: Array<{
		id: string
		url: string
		alt: string
		top: number
		left: number
		width: number
		height: number
		depth: number
		zIndex: number
		borderRadius?: number
	}>
	backgroundColor?: string
	sensitivity?: number
	minHeight?: number
	paddingTop?: number
	paddingBottom?: number
}

interface RelatedContentBlockContent {
	title?: string
	highlightWords?: string
	caseStudyIds?: string[]
	caseStudies?: Array<{
		id: string
		title: string
		slug: string
		client?: string | null
		imageUrl?: string | null
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
	}>
	paddingTop?: number
	paddingBottom?: number
}

interface NextCasesBlockContent {
	title?: string
	highlightWords?: string
	caseStudyIds?: string[]
	limit?: number // Number of cases to display (1-6)
	aspectRatio?: '4:3' | '3:2' | '16:9' | '1:1' | '3:4' | '2:3'
	caseStudies?: Array<{
		id: string
		title: string
		slug: string
		client?: string | null
		imageUrl?: string | null
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
	}>
	paddingTop?: number
	paddingBottom?: number
}

interface NewsOverviewBlockContent {
	title?: string
	highlightWords?: string
	showFilters?: boolean
	rows?: number
	aspectRatio?: '4:3' | '3:2' | '16:9' | '1:1' | '3:4'
	newsArticles?: Array<{
		id: string
		title: string
		slug: string
		excerpt?: string | null
		imageUrl?: string | null
		category?: string | null
		publishedAt?: string | null
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
	}>
	paddingTop?: number
	paddingBottom?: number
}

interface ServicesListBlockContent {
	title?: string
	highlightWords?: string
	items: Array<{
		image?: string
		title: string
		description: string
	}>
	paddingTop?: number
	paddingBottom?: number
}

interface ContentSidebarBlockContent {
	sections: Array<{
		title: string
		description: string
	}>
	sidebarTitle?: string
	sidebarItems: Array<{
		label: string
	}>
	paddingTop?: number
	paddingBottom?: number
}

type BlockContent =
	| HeroBlockContent
	| HeroIndexBlockContent
	| ContactHeroBlockContent
	| TextBlockContent
	| ImageGridBlockContent
	| CardCarouselBlockContent
	| LongTextBlockContent
	| ProjectCarouselBlockContent
	| CasesOverviewBlockContent
	| QuoteCarouselBlockContent
	| SectionImageBlockContent
	| VideoBlockContent
	| FloatingImagesBlockContent
	| RelatedContentBlockContent
	| NextCasesBlockContent
	| NewsOverviewBlockContent
	| ServicesListBlockContent
	| ContentSidebarBlockContent

interface Block {
	id: string
	type: string
	order: number
	content: string
}

interface BlockRendererProps {
	blocks: Block[]
	services?: Array<{
		id: string
		name: string
		slug: string
		description?: string | null
	}>
	sectors?: Array<{
		id: string
		name: string
		slug: string
	}>
	caseStudies?: Array<{
		id: string
		title: string
		slug: string
		client?: string | null
		imageUrl?: string | null
		imageAlt?: string | null
		featured?: boolean
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
		services?: Array<{ id: string; name: string; slug: string }>
		sectors?: Array<{ id: string; name: string; slug: string }>
	}>
	newsArticles?: Array<{
		id: string
		title: string
		slug: string
		excerpt?: string | null
		imageUrl?: string | null
		category?: string | null
		publishedAt?: string | null
		videoMode?: 'none' | 'hover' | 'autoplay'
		videoPlaybackId?: string
	}>
	currentServiceSlug?: string | null
}

// Helper to get background info from any block type
function getBlockBackgroundInfo(block: { type: string; content: string }): {
	isDark: boolean
	color: string | undefined
} {
	try {
		const content = JSON.parse(block.content || '{}') as Record<string, unknown>

		switch (block.type) {
			case 'hero': {
				const bgColor = (content.backgroundColor as string) || '#231a1a'
				// If there's a background image, assume it needs light text (dark overlay)
				if (content.backgroundImage) {
					return { isDark: true, color: bgColor }
				}
				return { isDark: isColorDark(bgColor), color: bgColor }
			}
			case 'text':
			case 'long-text': {
				// Text blocks have #f7f1e2 background (light)
				return { isDark: false, color: '#f7f1e2' }
			}
			case 'section-image':
			case 'video': {
				// Section images and videos are on white background
				return { isDark: false, color: '#ffffff' }
			}
			case 'image-grid':
			case 'card-carousel':
			case 'project-carousel':
			case 'cases-overview': {
				// These blocks are typically on white/light backgrounds
				return { isDark: false, color: '#ffffff' }
			}
			case 'hero-index':
			case 'quote-carousel':
			case 'floating-images': {
				// These have cream/light background by default
				const bgColor = (content.backgroundColor as string) || '#f7f1e2'
				return { isDark: isColorDark(bgColor), color: bgColor }
			}
			case 'contact-hero': {
				// Contact hero has dark background by default
				const bgColor = (content.backgroundColor as string) || '#231a1a'
				return { isDark: isColorDark(bgColor), color: bgColor }
			}
			default:
				return { isDark: false, color: undefined }
		}
	} catch {
		return { isDark: false, color: undefined }
	}
}

export function BlockRenderer({
	blocks,
	services = [],
	sectors = [],
	caseStudies = [],
	newsArticles = [],
	currentServiceSlug = null,
}: BlockRendererProps) {
	const { setNavigationTheme } = useNavigationTheme()

	// Determine navigation theme based on the FIRST block's background color (by order)
	const firstBlockBackgroundInfo = useMemo(() => {
		if (!blocks || blocks.length === 0) {
			return { isDark: false, color: undefined }
		}

		// Sort blocks by order and get the first one
		const sortedBlocks = [...blocks].sort((a, b) => a.order - b.order)
		const firstBlock = sortedBlocks[0]

		if (!firstBlock) {
			return { isDark: false, color: undefined }
		}

		return getBlockBackgroundInfo(firstBlock)
	}, [blocks])

	// Update navigation theme when first block changes
	useEffect(() => {
		setNavigationTheme(
			firstBlockBackgroundInfo.isDark,
			firstBlockBackgroundInfo.color,
		)

		// Reset navigation theme on unmount
		return () => {
			setNavigationTheme(false, undefined)
		}
	}, [
		firstBlockBackgroundInfo.isDark,
		firstBlockBackgroundInfo.color,
		setNavigationTheme,
	])

	return (
		<div className="block-renderer">
			{blocks.map((block, index) => {
				const content = JSON.parse(block.content || '{}') as BlockContent
				// First block (hero-index) can have overflow-visible, subsequent blocks need relative positioning
				// to ensure they stack above the hero's bleeding gradient blobs
				const isHeroIndex = block.type === 'hero-index'
				const isHeroWithBlob =
					block.type === 'hero' && (content as any).showGradientBlob
				const hasBlobOverflow = isHeroIndex || isHeroWithBlob
				return (
					<div
						key={block.id}
						className={cn(
							'block-item',
							!hasBlobOverflow && index > 0 && 'relative z-[1]',
						)}
					>
						{renderBlock(block.type, content, {
							services,
							sectors,
							caseStudies,
							newsArticles,
							currentServiceSlug,
						})}
					</div>
				)
			})}
		</div>
	)
}

function renderBlock(
	type: string,
	content: BlockContent,
	data: {
		services: Array<{
			id: string
			name: string
			slug: string
			description?: string | null
		}>
		sectors: Array<{
			id: string
			name: string
			slug: string
		}>
		caseStudies: Array<{
			id: string
			title: string
			slug: string
			client?: string | null
			imageUrl?: string | null
			imageAlt?: string | null
			featured?: boolean
			videoMode?: 'none' | 'hover' | 'autoplay'
			videoPlaybackId?: string
			services?: Array<{ id: string; name: string; slug: string }>
			sectors?: Array<{ id: string; name: string; slug: string }>
		}>
		newsArticles: Array<{
			id: string
			title: string
			slug: string
			excerpt?: string | null
			imageUrl?: string | null
			category?: string | null
			publishedAt?: string | null
			videoMode?: 'none' | 'hover' | 'autoplay'
			videoPlaybackId?: string
		}>
		currentServiceSlug?: string | null
	},
) {
	switch (type) {
		case 'hero':
			return <HeroBlock content={content as HeroBlockContent} />
		case 'text':
			return <TextBlock content={content as TextBlockContent} />
		case 'long-text':
			return <LongTextBlock content={content as LongTextBlockContent} />
		case 'image-grid':
			return <ImageGridBlock content={content as ImageGridBlockContent} />
		case 'card-carousel':
			return <CardCarouselBlock content={content as CardCarouselBlockContent} />
		case 'project-carousel':
			return (
				<ProjectCarouselBlock
					content={{
						...(content as ProjectCarouselBlockContent),
						caseStudies: data.caseStudies,
					}}
				/>
			)
		case 'cases-overview':
			return (
				<CasesOverviewBlock
					content={{
						...(content as CasesOverviewBlockContent),
						caseStudies: data.caseStudies,
						services: data.services,
						sectors: data.sectors,
						currentServiceSlug: data.currentServiceSlug,
					}}
				/>
			)
		case 'section-image':
			return <SectionImageBlock content={content as SectionImageBlockContent} />
		case 'video':
			return <VideoBlock content={content as VideoBlockContent} />
		case 'hero-index':
			return <HeroIndexBlock content={content as HeroIndexBlockContent} />
		case 'contact-hero':
			return <ContactHeroBlock content={content as ContactHeroBlockContent} />
		case 'quote-carousel':
			return (
				<QuoteCarouselBlock content={content as QuoteCarouselBlockContent} />
			)
		case 'floating-images':
			return (
				<FloatingImagesBlock content={content as FloatingImagesBlockContent} />
			)
		case 'related-content':
			return (
				<RelatedContentBlock
					content={{
						...(content as RelatedContentBlockContent),
						caseStudies: data.caseStudies,
					}}
				/>
			)
		case 'next-cases':
			return (
				<NextCasesBlock
					content={{
						...(content as NextCasesBlockContent),
						caseStudies: data.caseStudies,
					}}
				/>
			)
		case 'news-overview':
			return (
				<NewsOverviewBlock
					content={{
						...(content as NewsOverviewBlockContent),
						newsArticles: data.newsArticles,
					}}
				/>
			)
		case 'services-list':
			return <ServicesListBlock content={content as ServicesListBlockContent} />
		case 'content-sidebar':
			return (
				<ContentSidebarBlock content={content as ContentSidebarBlockContent} />
			)
		default:
			return (
				<div className="text-muted-foreground py-8 text-center">
					Onbekend bloktype: {type}
				</div>
			)
	}
}

// Hero Block - Full width hero matching case study hero structure
function HeroBlock({ content }: { content: HeroBlockContent }) {
	const bgColor = content.backgroundColor || '#2B2625'
	const textColor = content.textColor || '#f7f1e2'
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const showGradientBlob = content.showGradientBlob ?? false

	// Calculate padding values based on settings (0-5 scale)
	// Add 8rem (2xl) base padding top for menu spacing, plus configurable padding
	const basePaddingTop = 8 // 8rem for menu spacing
	const additionalPaddingTop = [0, 1.5, 3, 4.5, 6, 8][paddingTop] ?? 3
	const paddingTopValue = `${basePaddingTop + additionalPaddingTop}rem`
	const paddingBottomValue =
		['0', '1.5rem', '3rem', '4.5rem', '6rem', '8rem'][paddingBottom] || '3rem'

	return (
		<section
			className={cn(
				'group relative px-6 md:px-12 lg:px-24',
				showGradientBlob ? 'overflow-visible' : 'overflow-hidden',
			)}
			style={{
				backgroundColor: showGradientBlob ? undefined : bgColor,
				backgroundImage:
					content.backgroundImage && content.videoMode !== 'autoplay'
						? `url(${getOptimizedBgUrl(content.backgroundImage, 2560)})`
						: undefined,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				paddingTop: paddingTopValue,
				paddingBottom: paddingBottomValue,
			}}
		>
			{/* Background video (autoplay) - replaces background image */}
			{content.videoMode === 'autoplay' && content.videoPlaybackId && (
				<VideoAsImage
					muxPlaybackId={content.videoPlaybackId}
					className="absolute inset-0 z-0"
				/>
			)}

			{/* Interactive gradient blob animation */}
			{showGradientBlob && (
				<InteractiveGradientBlob backgroundColor={bgColor} size="80%" />
			)}

			{/* Overlay for background images/video */}
			{(content.backgroundImage ||
				(content.videoMode === 'autoplay' && content.videoPlaybackId)) && (
				<div className="absolute inset-0 bg-black/40" />
			)}

			{/* Hover video overlay on background image */}
			{content.videoMode === 'hover' &&
				content.videoPlaybackId &&
				content.backgroundImage && (
					<HoverVideoOverlay muxPlaybackId={content.videoPlaybackId} />
				)}

			{/* Gradient overlay - only show when not using gradient blob */}
			{!showGradientBlob && (
				<div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent to-black/30" />
			)}

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.7 }}
				className={cn(
					'relative z-10 mx-auto',
					GRID_NARROW,
				)}
			>
				{/* Main Title */}
				<motion.h1
					initial={{ opacity: 0, y: 18 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
					className="mb-8 text-[40px] leading-none font-black tracking-[-0.02em] md:mb-6 md:text-[56px] lg:text-[76px]"
					style={{
						color: textColor,
						...CONDENSED_FONT_STYLE,
					}}
				>
					{renderHighlightedTitle(content.title, content.highlightWords)}
				</motion.h1>

				{/* Subtitle and Description */}
				{(content.subtitle || content.description) && (
					<motion.div
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 1,
							ease: [0.25, 0.46, 0.45, 0.94],
							delay: 0.15,
						}}
						className="max-w-5xl space-y-6"
					>
						{content.subtitle && (
							<h2
								className="text-[18px] leading-[1.6] md:text-[22px] lg:text-[24px]"
								style={{
									color: textColor,
									opacity: 0.95,
									fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
									fontWeight: 400,
									fontStretch: 'normal',
								}}
							>
								{content.subtitle}
							</h2>
						)}

						{content.description && (
							<p
								className="text-[16px] leading-[1.6] md:text-[20px] lg:text-[24px]"
								style={{
									color: textColor,
									opacity: 0.85,
									fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
									fontWeight: 400,
								}}
							>
								{content.description}
							</p>
						)}
					</motion.div>
				)}

				{/* Bottom Meta Information: Company name + Service tags + Award badge */}
				{(content.companyName ||
					(content.serviceLinks && content.serviceLinks.length > 0) ||
					content.showAwardBadge) && (
					<motion.div
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{
							duration: 1,
							ease: [0.25, 0.46, 0.45, 0.94],
							delay: 0.25,
						}}
						className="mt-16 flex flex-wrap items-center gap-6 pb-[1em] md:gap-8"
					>
						{/* Company Name */}
						{content.companyName && (
							<div
								className="text-[16px]"
								style={{
									color: textColor,
									opacity: 0.9,
									fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
									fontWeight: 600,
								}}
							>
								{content.companyName}
							</div>
						)}

						{/* Service Tags - clickable links */}
						{content.serviceLinks && content.serviceLinks.length > 0 && (
							<div
								className="flex items-center gap-4 text-[14px] md:gap-6 md:text-[15px]"
								style={{
									fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
									fontWeight: 400,
								}}
							>
								{content.serviceLinks.map((service, index) => (
									<Link
										key={index}
										to={`/diensten/${service.slug}`}
										className="underline-offset-2 transition-colors duration-200 hover:underline"
										style={{
											color: textColor,
											opacity: 0.7,
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.opacity = '0.9')
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.opacity = '0.7')
										}
									>
										{service.label}
									</Link>
								))}
							</div>
						)}

						{/* Award Badge - aligned to the right */}
						{content.showAwardBadge && (
							<div
								className="ml-auto flex items-center gap-2 text-[14px] md:text-[15px]"
								style={{
									color: textColor,
									opacity: 0.7,
									fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
								}}
							>
								<Icon name="award-symbol" className="h-6 w-6 md:h-7 md:w-7" />
								<span>Award winning</span>
							</div>
						)}
					</motion.div>
				)}
			</motion.div>
		</section>
	)
}

// Text Block - With optional title, columns, and quote
function TextBlock({ content }: { content: TextBlockContent }) {
	const isMultiCol = (content.columns || 1) > 1

	const alignmentClasses: Record<string, string> = {
		left: 'text-left',
		center: 'text-center mx-auto',
		right: 'text-right ml-auto',
	}

	// Line height mapping — values match the admin editor preview exactly
	const lineHeightClasses: Record<string, string> = {
		tight: '[&_p]:leading-[0.85] [&_span]:leading-[0.85]',
		snug: '[&_p]:leading-[0.95] [&_span]:leading-[0.95]',
		compact: '[&_p]:leading-[1.2] [&_span]:leading-[1.2]',
		normal: '[&_p]:leading-[1.4] [&_span]:leading-[1.4]',
		relaxed: '[&_p]:leading-[1.6] [&_span]:leading-[1.6]',
		loose: '[&_p]:leading-[1.8] [&_span]:leading-[1.8]',
	}

	// Multi-column classes using CSS columns (text flows across columns)
	const multiColClasses: Record<number, string> = {
		2: 'md:columns-2 gap-10',
		3: 'md:columns-2 lg:columns-3 gap-10',
	}

	const showQuote = content.showQuote && content.quote
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const alignment = content.alignment || 'left'
	const lineHeight = content.lineHeight || 'normal'

	return (
		<section
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<motion.div
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, margin: '-100px' }}
				variants={fadeInUp}
				className={cn(
					'mx-auto',
					showQuote || isMultiCol ? GRID_WIDE : GRID_NARROW,
				)}
			>
				{content.title && (
					<motion.div variants={staggerItem} className="mb-8">
						<h2
							className={cn(HEADING_CLASSES.h2, 'text-black')}
							style={CONDENSED_FONT_STYLE}
						>
							{renderHighlightedTitle(content.title, content.highlightWords)}
						</h2>
					</motion.div>
				)}

				{showQuote ? (
					// Two-column split layout: left=heading, right=content, 40px gap
					<div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 md:gap-10">
						{/* Quote - left column */}
						<motion.div variants={staggerItem}>
							<blockquote
								className={cn(HEADING_CLASSES.h2, 'text-black')}
								style={CONDENSED_FONT_STYLE}
							>
								{content.quote}
							</blockquote>
						</motion.div>

						{/* Content - right column */}
						<motion.div
							variants={staggerItem}
							className="flex flex-col gap-[26px]"
						>
							<style>{`
								.quote-content h1,
								.quote-content h2,
								.quote-content h3 {
									font-family: "Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif;
									font-stretch: condensed;
									font-weight: 900;
								}
							`}</style>
							<div
								className={cn(
									'quote-content max-w-none text-black',
									RICH_TEXT_HEADING_CLASSES,
									'[&_h1]:mt-0 [&_h1]:mb-6 [&_h1]:text-black',
									'[&_h2]:mt-6 [&_h2]:mb-4 [&_h2]:text-black',
									'[&_h3]:mt-4 [&_h3]:mb-3 [&_h3]:text-black',
									'[&_h4]:mt-0 [&_h4]:mb-4 [&_h4]:text-black',
									// Body text - base styles (inline styles from editor will override)
									'[&_p]:mt-0 [&_p]:mb-4 [&_p]:text-[16px] [&_p]:text-black [&_p]:md:text-[18px] [&_p]:lg:text-[20px]',
									// Spans with inline styles from rich text editor (font-size, line-height)
									'[&_span]:inline',
									'[&_a]:text-[#eb008b] [&_a]:no-underline hover:[&_a]:underline',
									'columns-1',
									alignmentClasses[alignment],
									lineHeightClasses[lineHeight],
								)}
								style={{
									fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
								}}
								dangerouslySetInnerHTML={{ __html: content.content }}
							/>
						</motion.div>
					</div>
				) : isMultiCol ? (
					// Multi-column layout using CSS columns (text flows across columns)
					<>
						<style>{`
							.text-block-content h1,
							.text-block-content h2,
							.text-block-content h3 {
								font-family: "Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif;
								font-stretch: condensed;
								font-weight: 900;
							}
						`}</style>
						<motion.div
							variants={staggerItem}
							className={cn(
								'text-block-content max-w-none text-black/80',
								RICH_TEXT_HEADING_CLASSES,
								'[&_h1]:mt-0 [&_h1]:mb-6 [&_h1]:text-black',
								'[&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-black',
								'[&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-black',
								'[&_p]:mb-4 [&_p]:text-[20px]',
								'[&_span]:inline',
								'[&_a]:text-[#eb008b] [&_a]:no-underline hover:[&_a]:underline',
								multiColClasses[content.columns || 2],
								alignmentClasses[alignment],
								lineHeightClasses[lineHeight],
							)}
							dangerouslySetInnerHTML={{ __html: content.content }}
						/>
					</>
				) : (
					// Standard single-column layout
					<>
						<style>{`
							.text-block-content h1,
							.text-block-content h2,
							.text-block-content h3 {
								font-family: "Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif;
								font-stretch: condensed;
								font-weight: 900;
							}
						`}</style>
						<motion.div
							variants={staggerItem}
							className={cn(
								'text-block-content max-w-none text-black/80',
								RICH_TEXT_HEADING_CLASSES,
								'[&_h1]:mt-0 [&_h1]:mb-6 [&_h1]:text-black',
								'[&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-black',
								'[&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-black',
								// Body text - base styles (inline styles from editor will override)
								'[&_p]:mb-4 [&_p]:text-[20px]',
								// Spans with inline styles from rich text editor (font-size, line-height)
								'[&_span]:inline',
								'[&_a]:text-[#eb008b] [&_a]:no-underline hover:[&_a]:underline',
								GRID_NARROW,
								alignmentClasses[alignment],
								lineHeightClasses[lineHeight],
							)}
							dangerouslySetInnerHTML={{ __html: content.content }}
						/>
					</>
				)}
			</motion.div>
		</section>
	)
}

// Long Text Block - Single column long-form content
function LongTextBlock({ content }: { content: LongTextBlockContent }) {
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2

	return (
		<section
			className={cn(
				'px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
			style={{
				backgroundColor: content.backgroundColor || '#f7f1e2',
			}}
		>
			<motion.div
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, margin: '-100px' }}
				variants={fadeInUp}
				className={cn('mx-auto', GRID_NARROW)}
			>
				{content.title && (
					<motion.h2
						variants={staggerItem}
						className={cn('mb-8', HEADING_CLASSES.h2, 'text-black')}
						style={CONDENSED_FONT_STYLE}
					>
						{renderHighlightedTitle(content.title, content.highlightWords)}
					</motion.h2>
				)}
				<style>{`
					.long-text-content h1,
					.long-text-content h2,
					.long-text-content h3 {
						font-family: "Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif;
						font-stretch: condensed;
						font-weight: 900;
					}
				`}</style>
				<motion.div
					variants={staggerItem}
					className={cn(
						'long-text-content max-w-none text-black/80',
						RICH_TEXT_HEADING_CLASSES,
						'[&_h1]:mt-0 [&_h1]:mb-6 [&_h1]:text-black',
						'[&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-black',
						'[&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-black',
						// Body text - proper spacing with readable line height
						'[&_p]:mb-6 [&_p]:text-[20px] [&_p]:leading-[1.6]',
						// Spans with inline styles from rich text editor (font-size, line-height)
						'[&_span]:inline',
						'[&_a]:text-[#eb008b] [&_a]:no-underline hover:[&_a]:underline',
					)}
					dangerouslySetInnerHTML={{ __html: content.content }}
				/>
			</motion.div>
		</section>
	)
}

// Image Grid Block - Responsive image grid matching Figma frames 43/44/45
// Figma: 40px side padding, 40px gap, rounded-2xl corners, no shadows
function ImageGridBlock({ content }: { content: ImageGridBlockContent }) {
	const columnClasses: Record<number, string> = {
		1: 'grid-cols-1',
		2: 'grid-cols-1 md:grid-cols-2',
		3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
		4: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
	}

	// Aspect ratio classes
	const aspectRatioClasses: Record<string, string> = {
		auto: '',
		'16:9': 'aspect-video',
		'4:3': 'aspect-[4/3]',
		'3:2': 'aspect-[3/2]',
		'1:1': 'aspect-square',
		'2:3': 'aspect-[2/3]',
		'9:16': 'aspect-[9/16]',
		'21:9': 'aspect-[21/9]',
	}

	if (!content.images || content.images.length === 0) {
		return null
	}

	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const aspectRatio = content.aspectRatio || '4:3'
	const aspectClass =
		aspectRatioClasses[aspectRatio] || aspectRatioClasses['4:3']
	const hasFixedAspect = aspectRatio !== 'auto'

	// Compute image dimensions based on columns for retina sharpness
	const columns = content.columns || 3
	const maxCssWidthByColumns: Record<number, number> = {
		1: 1920,
		2: 960,
		3: 640,
		4: 480,
	}
	const imgDims = getImgDimensions(
		maxCssWidthByColumns[columns] || 640,
		aspectRatio,
	)

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<div className="mx-auto max-w-[1920px]">
				{content.title && (
					<motion.h2
						variants={staggerItem}
						className={cn(
							'mb-12 text-center text-black',
							HEADING_CLASSES.h2,
						)}
					>
						{renderHighlightedTitle(content.title, content.highlightWords)}
					</motion.h2>
				)}
				<div className={cn('grid gap-10', columnClasses[columns])}>
					{content.images.map((image, index) => {
						const videoMode = image.videoMode || 'none'
						const hasVideo = videoMode !== 'none' && image.videoPlaybackId

						return (
							<motion.figure
								key={index}
								variants={staggerItem}
								className="group overflow-hidden rounded-2xl"
							>
								<div
									className={cn(
										'relative overflow-hidden rounded-2xl',
										hasFixedAspect && aspectClass,
									)}
								>
									{hasVideo && videoMode === 'autoplay' ? (
										<VideoAsImage
											muxPlaybackId={image.videoPlaybackId!}
											className="rounded-2xl"
										/>
									) : (
										<>
											<Img
												src={getOptimizedImageUrl(image.url) || ''}
												alt={image.alt}
												loading="lazy"
												width={imgDims.width}
												height={imgDims.height}
												className={cn(
													'w-full rounded-2xl object-cover transition-transform duration-500 group-hover:scale-105',
													hasFixedAspect ? 'h-full' : 'h-auto',
												)}
											/>
											{hasVideo && videoMode === 'hover' && (
												<HoverVideoOverlay
													muxPlaybackId={image.videoPlaybackId!}
													className="rounded-2xl"
												/>
											)}
										</>
									)}
								</div>
								{image.caption && (
									<figcaption className="p-4 text-sm text-black/60">
										{image.caption}
									</figcaption>
								)}
							</motion.figure>
						)
					})}
				</div>
			</div>
		</motion.section>
	)
}

// Card Carousel Block - Horizontally scrolling separate cards with icon, title, description, CTA
function CardCarouselBlock({ content }: { content: CardCarouselBlockContent }) {
	const cards = content.cards || []
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2

	const [emblaRef, emblaApi] = useEmblaCarousel({
		loop: cards.length > 1,
		duration: 45,
		dragFree: false,
		align: 'start',
		containScroll: 'trimSnaps',
	})
	const [selectedIndex, setSelectedIndex] = useState(0)

	const scrollTo = useCallback(
		(index: number) => {
			emblaApi?.scrollTo(index)
		},
		[emblaApi],
	)

	const onSelect = useCallback(() => {
		if (!emblaApi) return
		setSelectedIndex(emblaApi.selectedScrollSnap())
	}, [emblaApi])

	// Setup embla callbacks
	useEffect(() => {
		if (!emblaApi) return

		onSelect()
		emblaApi.on('select', onSelect)
		emblaApi.on('reInit', onSelect)

		return () => {
			emblaApi.off('select', onSelect)
			emblaApi.off('reInit', onSelect)
		}
	}, [emblaApi, onSelect])

	if (cards.length === 0) {
		return null
	}

	const showCarouselControls = cards.length > 1

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'overflow-hidden bg-[#f7f1e2]',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			{content.title && (
				<div className="px-6 md:px-12 lg:px-24">
					<motion.h2
						variants={staggerItem}
						className={cn(
							`mx-auto mb-8 ${GRID_NARROW} text-black`,
							HEADING_CLASSES.h2,
						)}
						style={CONDENSED_FONT_STYLE}
					>
						{renderHighlightedTitle(content.title, content.highlightWords)}
					</motion.h2>
				</div>
			)}

			{/* Horizontally scrolling cards */}
			<div ref={emblaRef} className="overflow-hidden">
				<div className="flex touch-pan-y">
					{cards.map((card, index) => (
						<motion.div
							key={index}
							variants={staggerItem}
							className={cn(
								'min-w-0 shrink-0 grow-0 basis-[80%] md:basis-[60%] lg:basis-[45%]',
								index === 0
									? 'pl-[max(1.5rem,calc((100vw-1176px)/2))] md:pl-[max(3rem,calc((100vw-1176px)/2))] lg:pl-[max(6rem,calc((100vw-1176px)/2))]'
									: 'pl-6 md:pl-10',
							)}
						>
							<CardContent card={card} />
						</motion.div>
					))}
				</div>
			</div>

			{/* Pagination dots - circle / pill (active) / circle */}
			{showCarouselControls && (
				<div className="mt-8 flex items-center justify-center gap-2.5">
					{cards.map((_, index) => (
						<button
							key={index}
							type="button"
							onClick={() => scrollTo(index)}
							className={cn(
								'h-4 rounded-full transition-all duration-300 ease-out',
								selectedIndex === index
									? 'w-11 bg-[#231a1a]'
									: 'w-4 bg-[#231a1a]/20 hover:bg-[#231a1a]/40',
							)}
							aria-label={
								selectedIndex === index ? 'Huidige slide' : 'Ga naar slide'
							}
						/>
					))}
				</div>
			)}
		</motion.section>
	)
}

// Card content component for CardCarouselBlock - individual white card
function CardContent({
	card,
}: {
	card: NonNullable<CardCarouselBlockContent['cards']>[number]
}) {
	return (
		<article className="flex h-full flex-col items-start rounded-2xl bg-white px-8 py-12 md:px-16 md:py-[100px]">
			{/* Icon - decorative at top-left */}
			{card.icon && (
				<Icon
					name={card.icon as any}
					className="mb-6 h-6 w-6 self-start text-black"
				/>
			)}

			{/* Card title - H2 left-aligned */}
			<h3
				className={cn(HEADING_CLASSES.h2, 'mb-6 text-black')}
				style={CONDENSED_FONT_STYLE}
			>
				{card.title}
			</h3>

			{/* Card description */}
			{card.description && (
				<p className="mb-6 text-[20px] leading-[1.4] text-black">
					{card.description}
				</p>
			)}

			{/* Card items list */}
			{card.items && card.items.length > 0 && (
				<ul className="mb-6 space-y-2 text-[20px] font-bold text-black">
					{card.items.map((item, i) => (
						<li key={i}>{item}</li>
					))}
				</ul>
			)}

			{/* Card image / video */}
			{card.image && (
				<div className="group mb-6 w-full">
					<div className="relative aspect-[4/3] overflow-hidden rounded-xl">
						{card.videoMode === 'autoplay' && card.videoPlaybackId ? (
							<VideoAsImage
								muxPlaybackId={card.videoPlaybackId}
								className="rounded-xl"
							/>
						) : (
							<>
								<Img
									src={getOptimizedImageUrl(card.image) || ''}
									alt={card.title}
									loading="lazy"
									width={1600}
									height={1200}
									className="h-full w-full object-cover"
								/>
								{card.videoMode === 'hover' && card.videoPlaybackId && (
									<HoverVideoOverlay
										muxPlaybackId={card.videoPlaybackId}
										className="rounded-xl"
									/>
								)}
							</>
						)}
					</div>
				</div>
			)}

			{/* CTA button - round gradient with arrow (samenwerken style) */}
			{card.link && (
				<Link to={card.link} className="mt-auto">
					<InteractiveHoverButton text={card.buttonText || 'Meer lezen'} />
				</Link>
			)}
		</article>
	)
}

// Project Carousel Block - Case study showcase
function ProjectCarouselBlock({
	content,
}: {
	content: ProjectCarouselBlockContent
}) {
	const caseStudies = content.caseStudies || []

	if (caseStudies.length === 0) {
		return null
	}

	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<div className="mx-auto max-w-[1400px]">
				{content.title && (
					<motion.h2
						variants={staggerItem}
						className={cn(
							'mb-12 text-center text-black',
							HEADING_CLASSES.h2,
						)}
					>
						{renderHighlightedTitle(content.title, content.highlightWords)}
					</motion.h2>
				)}
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					{caseStudies.map((caseStudy) => (
						<motion.article key={caseStudy.id} variants={staggerItem}>
							<Link
								to={`/cases/${caseStudy.slug}`}
								className="group block overflow-hidden rounded-2xl bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl"
							>
								{/* Case study image / video */}
								<div className="relative aspect-[4/3] overflow-hidden">
									{caseStudy.videoMode === 'autoplay' &&
									caseStudy.videoPlaybackId ? (
										<VideoAsImage
											muxPlaybackId={caseStudy.videoPlaybackId}
											className="rounded-t-2xl"
										/>
									) : caseStudy.imageUrl ? (
										<>
											<Img
												src={getOptimizedImageUrl(caseStudy.imageUrl) || ''}
												alt={caseStudy.title}
												loading="lazy"
												width={1400}
												height={1050}
												className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
											/>
											{caseStudy.videoMode === 'hover' &&
												caseStudy.videoPlaybackId && (
													<HoverVideoOverlay
														muxPlaybackId={caseStudy.videoPlaybackId}
														className="rounded-t-2xl"
													/>
												)}
										</>
									) : (
										<div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#eb008b] to-[#f6921e]">
											<span className="text-4xl font-black text-white opacity-30">
												{caseStudy.title.charAt(0)}
											</span>
										</div>
									)}

									{/* Gradient overlay */}
									<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

									{/* Title on hover */}
									<div className="absolute right-0 bottom-0 left-0 translate-y-4 p-6 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
										<h3 className="text-xl font-bold text-white">
											{caseStudy.title}
										</h3>
										{caseStudy.client && (
											<p className="mt-1 text-sm text-white/80">
												{caseStudy.client}
											</p>
										)}
									</div>
								</div>

								{/* Case study info */}
								<div className="p-4">
									<p className="text-sm font-medium text-black/60">
										{caseStudy.client || caseStudy.title}
									</p>
								</div>

								{/* Carousel dots */}
								<div className="flex items-center justify-center gap-1.5 pb-4">
									{[0, 1, 2].map((dot) => (
										<span
											key={dot}
											className={cn(
												'h-2 w-2 rounded-full transition-all duration-300',
												dot === 0 ? 'w-6 bg-[#231a1a]' : 'bg-[#231a1a]/20',
											)}
										/>
									))}
								</div>
							</Link>
						</motion.article>
					))}
				</div>
			</div>
		</motion.section>
	)
}

// Cases Overview Block - Figma design: full-width, two-column, full-width pattern
// Card height: 562px, border-radius: 32px, gap between rows: 40px
function CasesOverviewBlock({
	content,
}: {
	content: CasesOverviewBlockContent
}) {
	const allCaseStudies = content.caseStudies || []
	const services = content.services || []
	const sectors = content.sectors || []
	const filterType = content.filterType ?? 'service'
	// Backward compat: map 'rows' to 'limit5'
	const displayMode =
		(content.displayMode === 'rows' ? 'limit5' : content.displayMode) ??
		'limit5'

	// Fixed filter: when set, lock to a specific service/sector (no filter toolbar)
	const fixedServiceSlug = content.fixedServiceSlug || null
	const fixedSectorSlug = content.fixedSectorSlug || null
	const hasFixedFilter = !!fixedServiceSlug || !!fixedSectorSlug
	// Only show filter toolbar when no fixed filter is set
	const showFilters = !hasFixedFilter && (content.showFilters ?? true)

	// Initialize filter from loader data (handles shareable URLs like /cases?service=campagne)
	// When a fixed filter is set, use that instead
	const [currentServiceSlug, setCurrentServiceSlug] = useState<string | null>(
		fixedServiceSlug ?? content.currentServiceSlug ?? null,
	)
	const [currentSectorSlug, setCurrentSectorSlug] = useState<string | null>(
		fixedSectorSlug ?? null,
	)

	// Update service filter and URL without triggering React Router navigation
	const updateServiceFilter = useCallback((slug: string | null) => {
		setCurrentServiceSlug(slug)
		setCurrentSectorSlug(null) // Clear sector filter when setting service
		const url = new URL(window.location.href)
		if (slug) {
			url.searchParams.set('service', slug)
		} else {
			url.searchParams.delete('service')
		}
		url.searchParams.delete('sector')
		window.history.replaceState(null, '', url.toString())
	}, [])

	// Update sector filter and URL
	const updateSectorFilter = useCallback((slug: string | null) => {
		setCurrentSectorSlug(slug)
		setCurrentServiceSlug(null) // Clear service filter when setting sector
		const url = new URL(window.location.href)
		if (slug) {
			url.searchParams.set('sector', slug)
		} else {
			url.searchParams.delete('sector')
		}
		url.searchParams.delete('service')
		window.history.replaceState(null, '', url.toString())
	}, [])

	const clearFilters = useCallback(() => {
		setCurrentServiceSlug(null)
		setCurrentSectorSlug(null)
		const url = new URL(window.location.href)
		url.searchParams.delete('service')
		url.searchParams.delete('sector')
		window.history.replaceState(null, '', url.toString())
	}, [])

	const fullWidthAspectRatio = content.fullWidthAspectRatio ?? '21:9'
	const twoColumnAspectRatio = content.twoColumnAspectRatio ?? '4:3'
	const mobileAspectRatio = content.mobileAspectRatio ?? '4:3'

	const hasActiveFilter = !!currentServiceSlug || !!currentSectorSlug

	// Filter cases by active filter
	const filteredCases = currentServiceSlug
		? allCaseStudies.filter((c) =>
				c.services?.some((s) => s.slug === currentServiceSlug),
			)
		: currentSectorSlug
			? allCaseStudies.filter((c) =>
					c.sectors?.some((s) => s.slug === currentSectorSlug),
				)
			: allCaseStudies

	// Calculate initial cases based on display mode
	const initialCases =
		displayMode === 'unlimited'
			? filteredCases.slice(0, 5) // Start with 5, load more via lazy loading
			: filteredCases.slice(0, 5) // limit5 mode

	// Use lazy loading for unlimited mode
	const {
		cases: lazyCases,
		hasMore,
		isLoading,
		observerRef,
	} = useLazyCases({
		initialCases,
		serviceSlug: currentServiceSlug ?? undefined,
		sectorSlug: currentSectorSlug ?? undefined,
		enabled: displayMode === 'unlimited',
	})

	// Select which cases to display based on mode
	const caseStudies = displayMode === 'unlimited' ? lazyCases : initialCases

	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2

	// Determine which filter rows to show
	const showServiceFilters =
		showFilters &&
		(filterType === 'service' || filterType === 'both') &&
		services.length > 0
	const showSectorFilters =
		showFilters &&
		(filterType === 'sector' || filterType === 'both') &&
		sectors.length > 0

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<div className="mx-auto max-w-[1920px]">
				{content.title && (
					<motion.h2
						variants={staggerItem}
						className={cn('mb-6 text-black', HEADING_CLASSES.h1)}
						style={CONDENSED_FONT_STYLE}
					>
						{renderHighlightedTitle(content.title, content.highlightWords)}
					</motion.h2>
				)}

				{/* Filter Buttons */}
				{(showServiceFilters || showSectorFilters) && (
					<motion.div
						className="mb-12 flex flex-col gap-3"
						variants={staggerItem}
					>
						{/* Service filter row */}
						{showServiceFilters && (
							<div className="flex flex-wrap gap-2 md:gap-3">
								<button
									type="button"
									onClick={clearFilters}
									className={cn(
										'rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors duration-200 md:px-4 md:py-2 md:text-sm',
										!hasActiveFilter
											? 'text-white'
											: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
									)}
									style={{
										fontFamily:
											'"Helvetica Neue", Helvetica, Arial, sans-serif',
										...(!hasActiveFilter && {
											background:
												'linear-gradient(135deg, #eb008b 0%, #f6921e 100%)',
										}),
									}}
								>
									Alle cases
								</button>
								{services.map((service) => (
									<button
										type="button"
										key={service.id}
										onClick={() => updateServiceFilter(service.slug)}
										className={cn(
											'max-w-[180px] truncate rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors duration-200 md:max-w-none md:px-4 md:py-2 md:text-sm',
											currentServiceSlug === service.slug
												? 'text-white'
												: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
										)}
										style={{
											fontFamily:
												'"Helvetica Neue", Helvetica, Arial, sans-serif',
											...(currentServiceSlug === service.slug && {
												background:
													'linear-gradient(135deg, #eb008b 0%, #f6921e 100%)',
											}),
										}}
									>
										{service.name}
									</button>
								))}
							</div>
						)}
						{/* Sector filter row */}
						{showSectorFilters && (
							<div className="flex flex-wrap gap-2 md:gap-3">
								{!showServiceFilters && (
									<button
										type="button"
										onClick={clearFilters}
										className={cn(
											'rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors duration-200 md:px-4 md:py-2 md:text-sm',
											!hasActiveFilter
												? 'text-white'
												: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
										)}
										style={{
											fontFamily:
												'"Helvetica Neue", Helvetica, Arial, sans-serif',
											...(!hasActiveFilter && {
												background:
													'linear-gradient(135deg, #eb008b 0%, #f6921e 100%)',
											}),
										}}
									>
										Alle cases
									</button>
								)}
								{sectors.map((sector) => (
									<button
										type="button"
										key={sector.id}
										onClick={() => updateSectorFilter(sector.slug)}
										className={cn(
											'max-w-[180px] truncate rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors duration-200 md:max-w-none md:px-4 md:py-2 md:text-sm',
											currentSectorSlug === sector.slug
												? 'text-white'
												: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
										)}
										style={{
											fontFamily:
												'"Helvetica Neue", Helvetica, Arial, sans-serif',
											...(currentSectorSlug === sector.slug && {
												background:
													'linear-gradient(135deg, #eb008b 0%, #f6921e 100%)',
											}),
										}}
									>
										{sector.name}
									</button>
								))}
							</div>
						)}
					</motion.div>
				)}

				{caseStudies.length === 0 ? (
					<motion.div className="py-12 text-center" variants={staggerItem}>
						<p className="text-muted-foreground text-lg">
							Geen cases gevonden voor dit filter.
						</p>
						<button
							type="button"
							onClick={clearFilters}
							className="text-primary mt-4 inline-block hover:underline"
						>
							Bekijk alle cases
						</button>
					</motion.div>
				) : (
					<>
						{/* Gap between rows: 20px (gap-5) */}
						{/* Key forces re-mount + re-animation when filter changes */}
						<motion.div
							key={`cases-grid-${currentServiceSlug || 'all'}-${currentSectorSlug || 'all'}`}
							initial="initial"
							whileInView="animate"
							viewport={{ once: true, margin: '-50px' }}
							variants={staggerContainer}
							className="flex flex-col gap-5"
						>
							{caseStudies.map((caseStudy, index) => {
								// Figma pattern: 0 = full width, 1-2 = two columns, 3 = full width, 4-5 = two columns...
								const patternIndex = index % 4
								const isFullWidth = patternIndex === 0 || patternIndex === 3
								const isTwoColumnStart = patternIndex === 1
								const isTwoColumnEnd = patternIndex === 2

								// Lazy-loaded items (beyond initial batch) need their own
								// whileInView animation because the parent staggerContainer's
								// whileInView has already fired (once: true).
								const isLazyLoaded = index >= initialCases.length
								const animationProps = isLazyLoaded
									? {
											initial: { opacity: 0, y: 12 } as const,
											whileInView: { opacity: 1, y: 0 } as const,
											viewport: { once: true, margin: '-50px' } as const,
											transition: {
												duration: 0.7,
												ease: [0.25, 0.46, 0.45, 0.94] as const,
											},
										}
									: { variants: staggerItem }

								// For two-column items, group them together
								if (isTwoColumnStart) {
									const nextCase = caseStudies[index + 1]

									return (
										<motion.div
											key={caseStudy.id}
											{...animationProps}
											className="flex flex-col justify-between gap-5 md:flex-row md:gap-[20px]"
										>
											<div className="flex-1">
												<CasesOverviewCard
													caseStudy={caseStudy}
													aspectRatio={twoColumnAspectRatio}
													mobileAspectRatio={mobileAspectRatio}
												/>
											</div>
											{nextCase && (
												<div className="flex-1">
													<CasesOverviewCard
														caseStudy={nextCase}
														aspectRatio={twoColumnAspectRatio}
														mobileAspectRatio={mobileAspectRatio}
													/>
												</div>
											)}
										</motion.div>
									)
								}

								// Skip the second item of a two-column pair (already rendered above)
								if (isTwoColumnEnd) {
									return null
								}

								// Full width items
								if (isFullWidth) {
									return (
										<motion.div key={caseStudy.id} {...animationProps}>
											<CasesOverviewCard
												caseStudy={caseStudy}
												isFullWidth
												aspectRatio={fullWidthAspectRatio}
												mobileAspectRatio={mobileAspectRatio}
											/>
										</motion.div>
									)
								}

								return null
							})}
						</motion.div>

						{/* Infinite scroll sentinel and loading indicator for unlimited mode */}
						{displayMode === 'unlimited' && (
							<>
								{isLoading && (
									<div className="flex justify-center py-8">
										<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#eb008b]" />
									</div>
								)}
								{hasMore && !isLoading && (
									<div ref={observerRef} className="h-4" />
								)}
							</>
						)}
					</>
				)}
			</div>
		</motion.section>
	)
}

// Helper component for CasesOverviewBlock - Figma design pixel-perfect
// Card: h-[562px], rounded-[32px], px-6 py-4 for content, title 48px Condensed Black
// Meta: mt-4 (16px gap), px-6, client=Medium black 16px, services=Regular rgba(0,0,0,0.5) 16px, gap-4 between items
function CasesOverviewCard({
	caseStudy,
	isFullWidth = false,
	aspectRatio = '4:3',
	mobileAspectRatio = '4:3',
}: {
	caseStudy: NonNullable<CasesOverviewBlockContent['caseStudies']>[number]
	isFullWidth?: boolean
	aspectRatio?: string
	mobileAspectRatio?: string
}) {
	// Compute retina-ready dimensions based on card size and actual aspect ratio
	const imgDims = getImgDimensions(
		isFullWidth ? 1920 : 960,
		aspectRatio || '4:3',
	)

	return (
		<div className="flex flex-col gap-4">
			{/* Card image - clickable link to case detail */}
			<Link to={`/cases/${caseStudy.slug}`} className="group">
				<div
					style={
						{
							'--ar-mobile': mobileAspectRatio.replace(':', ' / '),
							'--ar-desktop': aspectRatio.replace(':', ' / '),
						} as React.CSSProperties
					}
					className={cn(
						'relative flex items-end overflow-hidden rounded-xl bg-[#d9d9d9]',
						'[aspect-ratio:var(--ar-mobile)] md:[aspect-ratio:var(--ar-desktop)]',
						isFullWidth && 'w-full',
					)}
				>
					{caseStudy.videoMode === 'autoplay' && caseStudy.videoPlaybackId ? (
						<VideoAsImage
							muxPlaybackId={caseStudy.videoPlaybackId}
							className="absolute inset-0 rounded-xl"
						/>
					) : caseStudy.imageUrl ? (
						<>
							<Img
								src={getOptimizedImageUrl(caseStudy.imageUrl) || ''}
								alt={caseStudy.imageAlt || caseStudy.title}
								loading="lazy"
								width={imgDims.width}
								height={imgDims.height}
								className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
							/>
							{caseStudy.videoMode === 'hover' && caseStudy.videoPlaybackId && (
								<HoverVideoOverlay
									muxPlaybackId={caseStudy.videoPlaybackId}
									className="rounded-xl"
								/>
							)}
						</>
					) : (
						<div className="absolute inset-0 bg-[#d9d9d9]" />
					)}
					{/* Gradient overlay for text readability - subtle fade */}
					<div className="absolute inset-0 bg-gradient-to-t from-black/[0.12] via-transparent to-transparent" />
					{/* Title at bottom-left - Figma: px-6 py-4, 48px Condensed Black */}
					<div className="relative z-10 px-6 py-4">
						<h3
							className="text-[28px] leading-none font-black text-white sm:text-[36px] md:text-[42px] lg:text-[48px]"
							style={{
								fontFamily:
									'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif',
								fontStretch: 'condensed',
								fontWeight: 900,
							}}
						>
							{caseStudy.title}
						</h3>
					</div>
				</div>
			</Link>
			{/* Meta info below card - outside the link so service tags can be independent links */}
			<div
				className="flex flex-wrap items-center gap-2 px-6 text-[11px] leading-[1.4] md:gap-4 md:text-[14px]"
				style={{
					fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
				}}
			>
				{/* Client name - Helvetica Neue Medium, black */}
				{caseStudy.client && (
					<span className="text-black" style={{ fontWeight: 500 }}>
						{caseStudy.client}
					</span>
				)}
				{/* Service tags - link to service detail pages */}
				{caseStudy.services &&
					caseStudy.services.length > 0 &&
					caseStudy.services.map((service) => (
						<Link
							key={service.id}
							to={`/diensten/${service.slug}`}
							className="max-w-[120px] truncate text-black/50 underline-offset-2 transition-colors duration-200 hover:text-black/70 hover:underline md:max-w-none"
							style={{ fontWeight: 400 }}
						>
							{service.name}
						</Link>
					))}
				{/* Sector tags */}
				{caseStudy.sectors &&
					caseStudy.sectors.length > 0 &&
					caseStudy.sectors.map((sector) => (
						<span
							key={sector.id}
							className="max-w-[120px] truncate text-black/50 md:max-w-none"
							style={{ fontWeight: 400 }}
						>
							{sector.name}
						</span>
					))}
			</div>
		</div>
	)
}

// Carousel dot component for section image carousel
function SectionImageDot({
	isActive,
	onClick,
}: {
	isActive: boolean
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'h-2.5 w-2.5 rounded-full transition-all duration-300 ease-out',
				isActive ? 'w-8 bg-[#1a1a1a]' : 'bg-[#1a1a1a]/20 hover:bg-[#1a1a1a]/40',
			)}
			aria-label={isActive ? 'Current slide' : 'Go to slide'}
		/>
	)
}

// Section Image Block - Full width single or multiple images with configurable padding
function SectionImageBlock({ content }: { content: SectionImageBlockContent }) {
	// Normalize images: support both legacy single url and new images array
	const images: SectionImageItem[] = useMemo(() => {
		if (content.images && content.images.length > 0) {
			return content.images
		}
		if (content.url) {
			return [{ url: content.url, alt: content.alt }]
		}
		return []
	}, [content.images, content.url, content.alt])

	const [emblaRef, emblaApi] = useEmblaCarousel({
		loop: images.length > 1,
		duration: 30,
		dragFree: false,
	})
	const [selectedIndex, setSelectedIndex] = useState(0)

	const scrollTo = useCallback(
		(index: number) => {
			emblaApi?.scrollTo(index)
		},
		[emblaApi],
	)

	const onSelect = useCallback(() => {
		if (!emblaApi) return
		setSelectedIndex(emblaApi.selectedScrollSnap())
	}, [emblaApi])

	// Setup embla callbacks
	useEffect(() => {
		if (!emblaApi) return

		onSelect()
		emblaApi.on('select', onSelect)
		emblaApi.on('reInit', onSelect)

		return () => {
			emblaApi.off('select', onSelect)
			emblaApi.off('reInit', onSelect)
		}
	}, [emblaApi, onSelect])

	if (images.length === 0) {
		return null
	}

	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const aspectRatio = content.aspectRatio || 'auto'

	// Map aspect ratios to CSS classes
	const aspectRatioClasses: Record<string, string> = {
		auto: '',
		'16:9': 'aspect-video',
		'4:3': 'aspect-[4/3]',
		'3:2': 'aspect-[3/2]',
		'1:1': 'aspect-square',
		'2:3': 'aspect-[2/3]',
		'9:16': 'aspect-[9/16]',
		'21:9': 'aspect-[21/9]',
	}

	const aspectClass = aspectRatioClasses[aspectRatio] || ''
	const hasFixedAspect = aspectRatio !== 'auto'

	// Compute retina-ready dimensions matching the selected aspect ratio
	const sectionImgDims = getImgDimensions(1920, aspectRatio)

	// When fullWidth is true, no horizontal padding and no rounded corners
	const isFullWidth = content.fullWidth ?? false
	const showCarouselControls = images.length > 1

	return (
		<section
			className={cn(
				'bg-[#f7f1e2]',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
				!isFullWidth && 'px-6 md:px-12 lg:px-24',
			)}
		>
			<motion.figure
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, margin: '-100px' }}
				variants={fadeInUp}
				className={cn('mx-auto', isFullWidth ? 'max-w-none' : 'max-w-[1920px]')}
			>
				{showCarouselControls ? (
					<>
						<div
							ref={emblaRef}
							className={cn('overflow-hidden', !isFullWidth && 'rounded-2xl')}
						>
							<div className="flex touch-pan-y">
								{images.map((image, index) => (
									<div
										key={index}
										className={cn(
											'min-w-0 shrink-0 grow-0 basis-full',
											hasFixedAspect && aspectClass,
										)}
									>
										<Img
											src={getOptimizedImageUrl(image.url) || ''}
											alt={image.alt || ''}
											loading="lazy"
											width={sectionImgDims.width}
											height={sectionImgDims.height}
											className={cn(
												'w-full object-cover',
												hasFixedAspect ? 'h-full' : 'h-auto',
											)}
										/>
									</div>
								))}
							</div>
						</div>
						{/* Carousel Dots */}
						<div className="mt-6 flex items-center justify-center gap-2">
							{images.map((_, index) => (
								<SectionImageDot
									key={index}
									isActive={selectedIndex === index}
									onClick={() => scrollTo(index)}
								/>
							))}
						</div>
					</>
				) : (
					<div
						className={cn(
							'group relative overflow-hidden',
							!isFullWidth && 'rounded-2xl',
							hasFixedAspect && aspectClass,
						)}
					>
						{content.videoMode === 'autoplay' && content.videoPlaybackId ? (
							<VideoAsImage
								muxPlaybackId={content.videoPlaybackId}
								className={cn(!isFullWidth && 'rounded-2xl')}
							/>
						) : (
							<>
								<Img
									src={getOptimizedImageUrl(images[0]!.url) || ''}
									alt={images[0]!.alt || ''}
									loading="lazy"
									width={sectionImgDims.width}
									height={sectionImgDims.height}
									className={cn(
										'w-full object-cover',
										hasFixedAspect ? 'h-full' : 'h-auto',
									)}
								/>
								{content.videoMode === 'hover' && content.videoPlaybackId && (
									<HoverVideoOverlay
										muxPlaybackId={content.videoPlaybackId}
										className={cn(!isFullWidth && 'rounded-2xl')}
									/>
								)}
							</>
						)}
					</div>
				)}
				{content.caption && (
					<figcaption
						className={cn(
							'mt-4 text-center text-sm text-black/60',
							isFullWidth && 'px-6 md:px-12 lg:px-24',
						)}
					>
						{content.caption}
					</figcaption>
				)}
			</motion.figure>
		</section>
	)
}

// Video Block - Full-width video with Mux player support
function VideoBlock({ content }: { content: VideoBlockContent }) {
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const isFullWidth = content.fullWidth ?? false
	const aspectRatio = content.aspectRatio || '16:9'

	// Map aspect ratios to CSS classes
	const aspectRatioClasses: Record<string, string> = {
		'16:9': 'aspect-video',
		'9:16': 'aspect-[9/16]',
		'4:3': 'aspect-[4/3]',
		'1:1': 'aspect-square',
		'21:9': 'aspect-[21/9]',
	}

	// Check if we have a video source
	const hasVideo = content.muxPlaybackId || content.videoUrl

	if (!hasVideo) {
		return null
	}

	return (
		<section
			className={cn(
				'bg-[#f7f1e2]',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
				!isFullWidth && 'px-6 md:px-12 lg:px-24',
			)}
		>
			<motion.figure
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, margin: '-100px' }}
				variants={fadeInUp}
				className={cn('mx-auto', isFullWidth ? 'max-w-none' : 'max-w-[1920px]')}
			>
				{content.title && (
					<h2
						className={cn(
							'mb-6 text-black',
							HEADING_CLASSES.h2,
							isFullWidth && 'px-6 md:px-12 lg:px-24',
						)}
					>
						{content.title}
					</h2>
				)}
				<div
					className={cn(
						'overflow-hidden bg-black',
						!isFullWidth && 'rounded-2xl',
						aspectRatioClasses[aspectRatio],
					)}
				>
					{content.muxPlaybackId ? (
						<IgniteVideoPlayer
							playbackId={content.muxPlaybackId}
							poster={
								content.posterUrl ||
								(content.thumbnailTime != null
									? `https://image.mux.com/${content.muxPlaybackId}/thumbnail.jpg?time=${content.thumbnailTime}&width=1920&height=1080&fit_mode=smartcrop`
									: undefined)
							}
							autoPlay={content.autoplay ? 'muted' : false}
							loop={content.loop}
							muted={content.muted || content.autoplay}
							aspectRatio={aspectRatio.replace(':', '/')}
						/>
					) : content.videoUrl ? (
						<video
							src={content.videoUrl}
							poster={content.posterUrl}
							autoPlay={content.autoplay}
							loop={content.loop}
							muted={content.muted || content.autoplay}
							controls
							playsInline
							className="h-full w-full object-cover"
						/>
					) : null}
				</div>
				{content.caption && (
					<figcaption
						className={cn(
							'mt-4 text-center text-sm text-black/60',
							isFullWidth && 'px-6 md:px-12 lg:px-24',
						)}
					>
						{content.caption}
					</figcaption>
				)}
			</motion.figure>
		</section>
	)
}

// Hero Index Block - Home page hero with gradient blob animation
function HeroIndexBlock({ content }: { content: HeroIndexBlockContent }) {
	const bgColor = content.backgroundColor || '#f7f1e2'
	const showGradientBlob = content.showGradientBlob ?? true
	const backgroundImage = content.backgroundImage
	const noFlexiblePaddingMobile = content.noFlexiblePaddingMobile ?? true

	// Fluid font sizing via clamp(min, preferred, max)
	const fontSizeMin = content.fontSizeMin ?? 40
	const fontSizePreferred = content.fontSizePreferred ?? 6
	const fontSizeMax = content.fontSizeMax ?? 96

	// Build title: prefer new `title` field, fall back to legacy gradientText + lines
	const resolvedTitle = content.title
		? content.title
		: [content.gradientText, ...(content.lines || [])].filter(Boolean).join(' ')

	// For highlight words: prefer new field, fall back to legacy gradientText as highlight
	const resolvedHighlightWords = content.highlightWords
		? content.highlightWords
		: content.gradientText && !content.title
			? content.gradientText.replace(/[,.]$/, '') // strip trailing punctuation for matching
			: undefined

	// Section classes based on noFlexiblePaddingMobile setting
	const sectionClassName = noFlexiblePaddingMobile
		? 'relative flex min-h-[60vh] items-center overflow-visible pt-32 pb-16'
		: 'relative flex min-h-[60vh] items-center overflow-visible pt-32 pb-16 md:min-h-[70vh] md:pt-40 md:pb-20'

	return (
		<section
			className={sectionClassName}
		>
			{/* Background layer - separate from section to allow blobs to show through */}
			<div
				className="absolute inset-0 -z-20"
				style={{ backgroundColor: bgColor }}
			/>

			{/* Background image layer */}
			{backgroundImage && (
				<div
					className="absolute inset-0 -z-15 bg-cover bg-center bg-no-repeat"
					style={{
						backgroundImage: `url(${getOptimizedBgUrl(backgroundImage)})`,
					}}
				/>
			)}

			{/* Interactive Mouse-Following Gradient Blob - extends beyond section for soft edge */}
			{showGradientBlob && (
				<div
					className="pointer-events-none absolute inset-x-0 top-0 -z-10"
					style={{
						height: content.blobContinuous ? '200%' : '140%',
						...(content.blobContinuous
							? {}
							: {
									maskImage:
										'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
									WebkitMaskImage:
										'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
								}),
					}}
				>
					<InteractiveGradientBlobHome
						containerClassName="h-full"
						firstColor="246, 146, 30"
						secondColor="235, 0, 139"
						pointerColor="255, 91, 142"
						size="80%"
						interactive={true}
						offsetX="25%"
						offsetY="15%"
					/>
				</div>
			)}

			{/* Content */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.7 }}
				className="relative mx-auto w-full max-w-[1920px] px-6 md:px-12 lg:px-24"
			>
				<div className="relative text-left">
					<h1
						className="relative z-[2] leading-none font-black tracking-[-0.005em] text-black"
						style={{
							...CONDENSED_FONT_STYLE,
							fontSize: `clamp(${fontSizeMin}px, ${fontSizePreferred}vw, ${fontSizeMax}px)`,
						}}
					>
						{renderHighlightedTitle(resolvedTitle, resolvedHighlightWords)}
					</h1>

					{content.tagline && (
						<p className="relative z-[2] mt-6 text-[1.125rem] font-normal text-black sm:text-[1.25rem] md:mt-8 md:text-[1.375rem] lg:text-[1.5rem]">
							{content.tagline}
						</p>
					)}
				</div>
			</motion.div>
		</section>
	)
}

// Contact Hero Block - Contact page with Schema.org structured data
function ContactHeroBlock({ content }: { content: ContactHeroBlockContent }) {
	const bgColor = content.backgroundColor || '#231a1a'
	const textColor = content.textColor || '#f7f1e2'
	const paddingTop = content.paddingTop ?? 5
	const paddingBottom = content.paddingBottom ?? 3

	// Default values matching the design
	const headlineLines = content.headlineLines || [
		'When, how',
		'what ;) laten we',
		'samenwerken',
	]
	const tagline =
		content.tagline || 'Fullservice communicatie met een sociale missie!'
	const subtagline =
		content.subtagline || 'Kom langs, bel ons of stuur een mail.'
	const companyName = content.companyName || 'Ignite Identity'
	const streetAddress = content.streetAddress || 'Damraklaan 8,'
	const postalCode = content.postalCode || '3544 PJ'
	const city = content.city || 'Utrecht'
	const country = content.country || 'The Netherlands'
	const telephone = content.telephone || '030 1287365129'
	const telephoneRaw = content.telephoneRaw || '+31301287365129'
	const email = content.email || 'robbert@igniteidentity.nl'
	const emailLabel = content.emailLabel || 'Mail Robbert'

	// Calculate padding values
	const basePaddingTop = 8 // 8rem for menu spacing
	const additionalPaddingTop = [0, 1.5, 3, 4.5, 6, 8][paddingTop] ?? 3
	const paddingTopValue = `${basePaddingTop + additionalPaddingTop}rem`
	const paddingBottomValue =
		['0', '1.5rem', '3rem', '4.5rem', '6rem', '8rem'][paddingBottom] || '4.5rem'

	// Schema.org JSON-LD structured data
	const schemaData = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: companyName,
		url: 'https://igniteidentity.nl',
		telephone: telephoneRaw,
		email: email,
		address: {
			'@type': 'PostalAddress',
			streetAddress: streetAddress.replace(/,$/, ''), // Remove trailing comma
			postalCode: postalCode,
			addressLocality: city,
			addressCountry: country === 'The Netherlands' ? 'NL' : country,
		},
		contactPoint: {
			'@type': 'ContactPoint',
			telephone: telephoneRaw,
			email: email,
			contactType: 'customer service',
			availableLanguage: ['Dutch', 'English'],
		},
	}

	return (
		<section
			className="relative overflow-hidden px-6 md:px-12 lg:px-24"
			style={{
				backgroundColor: bgColor,
				paddingTop: paddingTopValue,
				paddingBottom: paddingBottomValue,
			}}
			itemScope
			itemType="https://schema.org/Organization"
		>
			{/* Schema.org JSON-LD */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
			/>

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.7 }}
				className={cn('relative z-10 mx-auto', GRID_NARROW)}
			>
				{/* Large Italic Headline */}
				<motion.h1
					initial={{ opacity: 0, y: 18 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
					className="mb-16 leading-none font-black tracking-[-0.02em] md:mb-20"
					style={{
						color: textColor,
						fontFamily:
							'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif',
						fontStretch: 'condensed',
						fontWeight: 900,
						fontSize: 'clamp(40px, 8vw, 96px)',
					}}
				>
					{headlineLines.map((line, index) => (
						<span key={index}>
							{line}
							{index < headlineLines.length - 1 && <br />}
						</span>
					))}
				</motion.h1>

				{/* Two-column layout — aligned with GRID_WIDE */}
				<motion.div
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{
						duration: 1,
						ease: [0.25, 0.46, 0.45, 0.94],
						delay: 0.15,
					}}
					className="grid gap-8 md:grid-cols-[1fr_200px] md:gap-10"
				>
					{/* Left Column - Tagline */}
					<div>
						<h2
							className="mb-4 text-[18px] leading-[1.3] font-bold md:text-[22px] lg:text-[24px]"
							style={{
								color: textColor,
								fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
							}}
						>
							{tagline}
						</h2>
						<p
							className="text-[16px] leading-[1.4] md:text-[18px] lg:text-[20px]"
							style={{
								color: textColor,
								opacity: 0.85,
								fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
							}}
						>
							{subtagline}
						</p>
					</div>

					{/* Right Column - Contact Details with Schema.org microdata */}
					<div
						className="space-y-6"
						itemProp="address"
						itemScope
						itemType="https://schema.org/PostalAddress"
					>
						{/* Company Name */}
						<p
							className="text-[16px] font-medium md:text-[18px]"
							style={{
								color: textColor,
								fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
							}}
							itemProp="name"
						>
							{companyName}
						</p>

						{/* Address */}
						<address
							className="text-[16px] leading-[1.5] not-italic md:text-[18px]"
							style={{
								color: textColor,
								fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
							}}
						>
							<span itemProp="streetAddress">{streetAddress}</span>
							<br />
							<span itemProp="postalCode">{postalCode}</span>{' '}
							<span itemProp="addressLocality">{city}</span>
							<br />
							<span itemProp="addressCountry">{country}</span>
						</address>

						{/* Telephone - clickable */}
						<p
							className="text-[16px] md:text-[18px]"
							style={{
								color: textColor,
								fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
							}}
						>
							<span className="mr-1">T</span>
							<a
								href={`tel:${telephoneRaw}`}
								className="transition-opacity hover:opacity-80"
								itemProp="telephone"
								style={{ color: textColor }}
							>
								{telephone}
							</a>
						</p>

						{/* Email Button */}
						<a
							href={`mailto:${email}`}
							className="inline-block rounded-full px-8 py-3 text-[14px] font-medium transition-all hover:scale-105 hover:shadow-lg md:text-[16px]"
							style={{
								background: 'linear-gradient(135deg, #f6921e 0%, #eb008b 100%)',
								color: '#fff',
								fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
							}}
							itemProp="email"
						>
							{emailLabel}
						</a>
						{/* Hidden email for schema */}
						<meta itemProp="email" content={email} />
					</div>
				</motion.div>
			</motion.div>
		</section>
	)
}

// Quote Carousel Block - Testimonial carousel with autoplay
function QuoteCarouselBlock({
	content,
}: {
	content: QuoteCarouselBlockContent
}) {
	const quotes = content.quotes || []
	const bgColor = content.backgroundColor || '#f7f1e2'
	const autoplayDelay = content.autoplayDelay || 6000
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2

	const [emblaRef, emblaApi] = useEmblaCarousel({
		loop: quotes.length > 1,
		duration: 30,
		dragFree: false,
	})
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [isAutoplayPaused, setIsAutoplayPaused] = useState(false)

	const scrollTo = useCallback(
		(index: number) => {
			emblaApi?.scrollTo(index)
		},
		[emblaApi],
	)

	const onSelect = useCallback(() => {
		if (!emblaApi) return
		setSelectedIndex(emblaApi.selectedScrollSnap())
	}, [emblaApi])

	// Setup embla callbacks
	useEffect(() => {
		if (!emblaApi) return

		onSelect()
		emblaApi.on('select', onSelect)
		emblaApi.on('reInit', onSelect)

		return () => {
			emblaApi.off('select', onSelect)
			emblaApi.off('reInit', onSelect)
		}
	}, [emblaApi, onSelect])

	// Autoplay functionality
	useEffect(() => {
		if (!emblaApi || quotes.length <= 1 || isAutoplayPaused) return

		const autoplay = setInterval(() => {
			if (emblaApi.canScrollNext()) {
				emblaApi.scrollNext()
			} else {
				emblaApi.scrollTo(0)
			}
		}, autoplayDelay)

		return () => clearInterval(autoplay)
	}, [emblaApi, autoplayDelay, quotes.length, isAutoplayPaused])

	const handleInteractionStart = useCallback(() => {
		setIsAutoplayPaused(true)
	}, [])

	const handleInteractionEnd = useCallback(() => {
		setTimeout(() => setIsAutoplayPaused(false), 2000)
	}, [])

	if (quotes.length === 0) return null

	const showCarouselControls = quotes.length > 1

	return (
		<section
			className={cn(
				'px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
			style={{ backgroundColor: bgColor }}
		>
			<motion.div
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, margin: '-100px' }}
				variants={fadeInUp}
				className="mx-auto max-w-7xl"
				onMouseEnter={handleInteractionStart}
				onMouseLeave={handleInteractionEnd}
				onTouchStart={handleInteractionStart}
				onTouchEnd={handleInteractionEnd}
			>
				{showCarouselControls ? (
					<div ref={emblaRef} className="overflow-hidden">
						<div className="flex touch-pan-y">
							{quotes.map((quote, index) => (
								<div
									key={quote.id}
									className="min-w-0 shrink-0 grow-0 basis-full"
								>
									<QuoteContent
										quote={quote}
										isVisible={selectedIndex === index}
									/>
								</div>
							))}
						</div>
					</div>
				) : (
					<QuoteContent quote={quotes[0]!} isVisible={true} />
				)}

				{/* Carousel Dots */}
				{showCarouselControls && (
					<div className="mt-8 flex items-center justify-center gap-2 md:mt-12">
						{quotes.map((_, index) => (
							<button
								key={index}
								type="button"
								onClick={() => scrollTo(index)}
								className={cn(
									'h-2.5 w-2.5 rounded-full transition-all duration-300 ease-out',
									selectedIndex === index
										? 'w-8 bg-[#1a1a1a]'
										: 'bg-[#1a1a1a]/20 hover:bg-[#1a1a1a]/40',
								)}
								aria-label={
									selectedIndex === index ? 'Current slide' : 'Go to slide'
								}
							/>
						))}
					</div>
				)}
			</motion.div>
		</section>
	)
}

// Quote content component for QuoteCarouselBlock
function QuoteContent({
	quote,
	isVisible,
}: {
	quote: NonNullable<QuoteCarouselBlockContent['quotes']>[number]
	isVisible: boolean
}) {
	return (
		<motion.div
			initial={false}
			animate={{
				opacity: isVisible ? 1 : 0,
			}}
			transition={{
				duration: 0.7,
				ease: [0.25, 0.46, 0.45, 0.94],
			}}
			className="mx-auto flex max-w-5xl flex-col items-center gap-10 md:flex-row md:items-center md:gap-16 lg:gap-20"
			aria-hidden={!isVisible}
		>
			{/* Avatar Section */}
			{quote.image && (
				<div className="flex-shrink-0">
					<Img
						src={getOptimizedImageUrl(quote.image) || ''}
						alt={quote.author}
						loading="lazy"
						width={320}
						height={320}
						className="h-48 w-auto object-contain md:h-64 lg:h-80"
					/>
				</div>
			)}

			{/* Quote Content */}
			<div className="flex-1">
				<blockquote className="text-xl leading-relaxed text-black md:text-2xl lg:text-3xl">
					{quote.text}
				</blockquote>

				<div className="mt-8 md:mt-10">
					<p className="text-lg text-black md:text-xl">
						{quote.author}
						{quote.company && (
							<span className="text-black/60">, {quote.company}</span>
						)}
					</p>
				</div>
			</div>
		</motion.div>
	)
}

// Floating Images Block - Parallax floating images with mouse effect
// Design: Two rows of images that span edge-to-edge with staggered heights
// Images scale proportionally with viewport to maintain overlapping at all sizes
function FloatingImagesBlock({
	content,
}: {
	content: FloatingImagesBlockContent
}) {
	const images = content.images || []
	const bgColor = content.backgroundColor || '#f7f1e2'
	const sensitivity = content.sensitivity || 1
	const minHeight = content.minHeight || 700
	const paddingTop = content.paddingTop ?? 0
	const paddingBottom = content.paddingBottom ?? 0

	if (images.length === 0) return null

	// Design reference width for calculating proportions (matches Figma 1512px viewport)
	const designWidth = 1512

	return (
		<section
			className={cn(
				'relative w-full overflow-hidden',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
			style={{
				backgroundColor: bgColor,
				aspectRatio: `${designWidth} / ${minHeight}`,
			}}
		>
			{/* Centered container with max-width to contain the floating images */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.7 }}
				className="relative mx-auto h-full w-full max-w-[1600px]"
			>
				<Floating sensitivity={sensitivity} className="overflow-visible">
					{images.map((image, index) => {
						// Calculate width as percentage of container based on design reference
						// This ensures images scale proportionally and maintain overlaps
						const widthPercent = (image.width / designWidth) * 100

						return (
							<FloatingElement
								key={image.id}
								depth={image.depth}
								className="transition-transform duration-200 hover:scale-[1.02]"
								style={{
									top: `${image.top}%`,
									left: `${image.left}%`,
									zIndex: image.zIndex,
									// Use percentage width relative to container
									width: `${widthPercent}%`,
								}}
							>
								<motion.div
									initial={{ opacity: 0, scale: 0.95 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{
										duration: 0.8,
										delay: index * 0.12,
										ease: [0.25, 0.46, 0.45, 0.94],
									}}
									className=""
									style={{
										borderRadius: image.borderRadius ?? 0,
										overflow: 'hidden',
									}}
								>
									<Img
										src={getOptimizedImageUrl(image.url) || ''}
										alt={image.alt}
										loading="lazy"
										width={image.width}
										height={image.height}
										className="h-auto w-full object-cover"
										style={{
											aspectRatio: `${image.width} / ${image.height}`,
											borderRadius: image.borderRadius ?? 0,
										}}
									/>
								</motion.div>
							</FloatingElement>
						)
					})}
				</Floating>
			</motion.div>
		</section>
	)
}

// Related Content Block - 3 column grid of related case studies
function RelatedContentBlock({
	content,
}: {
	content: RelatedContentBlockContent
}) {
	const caseStudies = content.caseStudies || []
	const title = content.title || 'Gerelateerde content'
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2

	// Filter case studies by selected IDs if provided
	const displayCaseStudies = content.caseStudyIds?.length
		? caseStudies.filter((cs) => content.caseStudyIds?.includes(cs.id))
		: caseStudies.slice(0, 3)

	if (displayCaseStudies.length === 0) return null

	// If only 2 items, use 2-column layout with larger cards
	const isTwoItems = displayCaseStudies.length === 2

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<div className="mx-auto max-w-[1600px]">
				{/* Section header */}
				<motion.div variants={staggerItem} className="mb-8">
					<h2
						className={cn(HEADING_CLASSES.h2, 'text-black')}
						style={CONDENSED_FONT_STYLE}
					>
						{renderHighlightedTitle(title, content.highlightWords)}
					</h2>
				</motion.div>

				{/* Grid - 2 columns for 2 items, 3 columns for 3+ items */}
				<div
					className={cn(
						'grid gap-6',
						isTwoItems ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3',
					)}
				>
					{displayCaseStudies.map((caseStudy) => (
						<motion.div key={caseStudy.id} variants={staggerItem}>
							<Link
								to={`/cases/${caseStudy.slug}`}
								className="group flex flex-col gap-4"
							>
								{/* Card container - taller for 2-item layout */}
								<div
									className={cn(
										'relative flex items-end overflow-hidden rounded-[24px] bg-[#d9d9d9]',
										isTwoItems
											? 'h-[320px] sm:h-[400px] md:h-[480px]'
											: 'h-[280px] sm:h-[320px] md:h-[360px]',
									)}
								>
									{caseStudy.videoMode === 'autoplay' && caseStudy.videoPlaybackId ? (
										<VideoAsImage
											muxPlaybackId={caseStudy.videoPlaybackId}
											className="absolute inset-0 rounded-[24px]"
										/>
									) : caseStudy.imageUrl ? (
										<>
											<Img
												src={getOptimizedImageUrl(caseStudy.imageUrl) || ''}
												alt={caseStudy.title}
												loading="lazy"
												width={800}
												height={600}
												className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
											/>
											{caseStudy.videoMode === 'hover' && caseStudy.videoPlaybackId && (
												<HoverVideoOverlay
													muxPlaybackId={caseStudy.videoPlaybackId}
													className="rounded-[24px]"
												/>
											)}
										</>
									) : (
										<div className="absolute inset-0 bg-[#d9d9d9]" />
									)}
									{/* Gradient overlay for text readability */}
									<div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
									{/* Title at bottom-left */}
									<div className="relative z-10 px-5 py-4">
										<h3
											className={cn(
												'leading-none font-black text-white',
												isTwoItems
													? 'text-[28px] sm:text-[32px] md:text-[40px]'
													: 'text-[24px] sm:text-[28px] md:text-[32px]',
											)}
											style={{
												fontFamily:
													'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif',
												fontStretch: 'condensed',
												fontWeight: 900,
											}}
										>
											{caseStudy.title}
										</h3>
									</div>
								</div>
								{/* Meta info below card - matching AnimatedCaseCard styling */}
								<div className="flex items-baseline gap-3">
									{caseStudy.client && (
										<span
											className="text-foreground font-bold"
											style={{
												fontFamily:
													'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBold", "Helvetica Neue", Helvetica, Arial, sans-serif',
												fontStretch: 'condensed',
											}}
										>
											{caseStudy.client}
										</span>
									)}
								</div>
							</Link>
						</motion.div>
					))}
				</div>
			</div>
		</motion.section>
	)
}

// Next Cases Block - grid of next case studies to view
function NextCasesBlock({ content }: { content: NextCasesBlockContent }) {
	const caseStudies = content.caseStudies || []
	const title = content.title || 'Relevante cases'
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const limit = content.limit ?? 2 // Default to 2 cases

	// Filter case studies by selected IDs if provided, then apply limit
	let displayCaseStudies = content.caseStudyIds?.length
		? caseStudies.filter((cs) => content.caseStudyIds?.includes(cs.id))
		: caseStudies

	// Apply limit setting
	displayCaseStudies = displayCaseStudies.slice(0, limit)

	if (displayCaseStudies.length === 0) return null

	// Determine grid columns based on number of items
	const itemCount = displayCaseStudies.length
	const isTwoItems = itemCount <= 2
	const isThreeItems = itemCount === 3

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			{/* Match hero block max-width: max-w-[1280px] with same padding */}
			<div className="mx-auto max-w-[1280px]">
				{/* Section header */}
				<motion.div variants={staggerItem} className="mb-8">
					<h3
						className={cn(HEADING_CLASSES.h3, 'text-black')}
						style={CONDENSED_FONT_STYLE}
					>
						{renderHighlightedTitle(title, content.highlightWords)}
					</h3>
				</motion.div>

				{/* Grid - responsive columns based on item count */}
				<div
					className={cn(
						'grid gap-6',
						isTwoItems
							? 'md:grid-cols-2'
							: isThreeItems
								? 'md:grid-cols-2 lg:grid-cols-3'
								: 'md:grid-cols-2 lg:grid-cols-3',
					)}
				>
					{displayCaseStudies.map((caseStudy) => (
						<motion.div key={caseStudy.id} variants={staggerItem}>
							<Link
								to={`/cases/${caseStudy.slug}`}
								className="group flex flex-col gap-4"
							>
								{/* Card container - reduced border-radius from 24px to 16px */}
								<div
									className={cn(
										'relative flex items-end overflow-hidden rounded-xl bg-[#d9d9d9]',
										isTwoItems
											? 'h-[320px] sm:h-[400px] md:h-[480px]'
											: 'h-[280px] sm:h-[320px] md:h-[360px]',
									)}
								>
									{caseStudy.videoMode === 'autoplay' && caseStudy.videoPlaybackId ? (
										<VideoAsImage
											muxPlaybackId={caseStudy.videoPlaybackId}
											className="absolute inset-0 rounded-xl"
										/>
									) : caseStudy.imageUrl ? (
										<>
											<Img
												src={getOptimizedImageUrl(caseStudy.imageUrl) || ''}
												alt={caseStudy.title}
												loading="lazy"
												width={800}
												height={600}
												className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
											/>
											{caseStudy.videoMode === 'hover' && caseStudy.videoPlaybackId && (
												<HoverVideoOverlay
													muxPlaybackId={caseStudy.videoPlaybackId}
													className="rounded-xl"
												/>
											)}
										</>
									) : (
										<div className="absolute inset-0 bg-[#d9d9d9]" />
									)}
									{/* Gradient overlay for text readability */}
									<div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
									{/* Title at bottom-left */}
									<div className="relative z-10 px-5 py-4">
										<h3
											className={cn(
												'leading-none font-black text-white',
												isTwoItems
													? 'text-[28px] sm:text-[32px] md:text-[40px]'
													: 'text-[24px] sm:text-[28px] md:text-[32px]',
											)}
											style={{
												fontFamily:
													'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif',
												fontStretch: 'condensed',
												fontWeight: 900,
											}}
										>
											{caseStudy.title}
										</h3>
									</div>
								</div>
								{/* Meta info below card - matching AnimatedCaseCard styling */}
								<div className="flex items-baseline gap-3 pl-5">
									{caseStudy.client && (
										<span
											className="text-foreground font-bold"
											style={{
												fontFamily:
													'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBold", "Helvetica Neue", Helvetica, Arial, sans-serif',
												fontStretch: 'condensed',
											}}
										>
											{caseStudy.client}
										</span>
									)}
								</div>
							</Link>
						</motion.div>
					))}
				</div>
			</div>
		</motion.section>
	)
}

// News Overview Block - Grid of news articles with alternating layout
function NewsOverviewBlock({ content }: { content: NewsOverviewBlockContent }) {
	const newsArticles = content.newsArticles || []
	const title = content.title || ''
	const showFilters = content.showFilters ?? false
	const rows = content.rows || 3
	const aspectRatio = content.aspectRatio || '4:3'
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2

	// Only show published articles
	const articles = newsArticles.filter((a) => a.publishedAt)

	if (articles.length === 0) return null

	// Calculate how many items we need based on rows
	// Pattern: row 1 = 2 items (2/3 + 1/3), row 2 = 3 items, row 3 = 2 items (1/3 + 2/3) (repeating)
	const getItemsForRows = (numRows: number) => {
		let total = 0
		for (let i = 0; i < numRows; i++) {
			const rowType = i % 3
			if (rowType === 0 || rowType === 2)
				total += 2 // 2-column rows
			else total += 3 // 3-column row
		}
		return total
	}

	const totalItems = getItemsForRows(rows)
	const displayArticles = articles.slice(0, totalItems)

	// Fixed height classes for all cards (consistent height across all rows)
	const fixedHeightClasses: Record<string, string> = {
		'4:3': 'h-[200px] sm:h-[280px] md:h-[320px] lg:h-[400px]',
		'3:2': 'h-[180px] sm:h-[250px] md:h-[300px] lg:h-[360px]',
		'16:9': 'h-[160px] sm:h-[220px] md:h-[260px] lg:h-[320px]',
		'1:1': 'h-[200px] sm:h-[280px] md:h-[340px] lg:h-[420px]',
		'3:4': 'h-[240px] sm:h-[320px] md:h-[400px] lg:h-[500px]',
	}

	// Render a single article card
	const ArticleCard = ({ article }: { article: (typeof articles)[0] }) => (
		<Link to={`/nieuws/${article.slug}`} className="group flex flex-col gap-4">
			{/* Image card with title inside */}
			<div
				className={cn(
					'relative flex items-end overflow-hidden rounded-xl bg-[#d9d9d9]',
					fixedHeightClasses[aspectRatio],
				)}
			>
				{article.videoMode === 'autoplay' && article.videoPlaybackId ? (
					<VideoAsImage
						muxPlaybackId={article.videoPlaybackId}
						className="absolute inset-0 rounded-xl"
					/>
				) : article.imageUrl ? (
					<>
						<Img
							src={getOptimizedImageUrl(article.imageUrl) || ''}
							alt={article.title}
							loading="lazy"
							width={800}
							height={600}
							className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
						/>
						{article.videoMode === 'hover' && article.videoPlaybackId && (
							<HoverVideoOverlay
								muxPlaybackId={article.videoPlaybackId}
								className="rounded-xl"
							/>
						)}
					</>
				) : null}
				{/* Gradient overlay for text readability - subtle fade */}
				<div className="absolute inset-0 bg-gradient-to-t from-black/[0.12] via-transparent to-transparent" />
				{/* Title at bottom-left */}
				<div className="relative z-10 px-6 py-4">
					<h3
						className="text-[20px] leading-none font-black text-white sm:text-[24px] md:text-[28px] lg:text-[36px]"
						style={{
							fontFamily:
								'"Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif',
							fontStretch: 'condensed',
							fontWeight: 900,
						}}
					>
						{article.title}
					</h3>
				</div>
			</div>
			{/* Category below card - matches CasesOverviewCard meta style */}
			<div
				className="px-6 text-[16px] leading-[1.4]"
				style={{
					fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
				}}
			>
				<span
					className="text-black/50"
					style={{ fontWeight: 400, fontStyle: 'italic' }}
				>
					{article.category || 'Artikel'}
				</span>
			</div>
		</Link>
	)

	// Generate rows with alternating layout
	const renderRows = () => {
		const rowElements: React.ReactNode[] = []
		let articleIndex = 0

		for (
			let rowNum = 0;
			rowNum < rows && articleIndex < displayArticles.length;
			rowNum++
		) {
			const rowType = rowNum % 3

			if (rowType === 0) {
				// Row type 1: 2/3 + 1/3 (large left, small right)
				const article1 = displayArticles[articleIndex]
				const article2 = displayArticles[articleIndex + 1]
				if (!article1) break
				articleIndex += article2 ? 2 : 1

				rowElements.push(
					<motion.div
						key={`row-${rowNum}`}
						initial={{ opacity: 0, y: 18 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.8, delay: rowNum * 0.12 }}
						className="grid grid-cols-1 gap-6 md:grid-cols-3"
					>
						<div className="md:col-span-2">
							<ArticleCard article={article1} />
						</div>
						{article2 && (
							<div className="md:col-span-1">
								<ArticleCard article={article2} />
							</div>
						)}
					</motion.div>,
				)
			} else if (rowType === 1) {
				// Row type 2: 3 equal columns
				const rowArticles = displayArticles.slice(
					articleIndex,
					articleIndex + 3,
				)
				if (rowArticles.length === 0) break
				articleIndex += rowArticles.length

				rowElements.push(
					<motion.div
						key={`row-${rowNum}`}
						initial={{ opacity: 0, y: 18 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.8, delay: rowNum * 0.12 }}
						className="grid grid-cols-1 gap-6 md:grid-cols-3"
					>
						{rowArticles.map((article) => (
							<ArticleCard key={article.id} article={article} />
						))}
					</motion.div>,
				)
			} else {
				// Row type 3: 1/3 + 2/3 (small left, large right)
				const article1 = displayArticles[articleIndex]
				const article2 = displayArticles[articleIndex + 1]
				if (!article1) break
				articleIndex += article2 ? 2 : 1

				rowElements.push(
					<motion.div
						key={`row-${rowNum}`}
						initial={{ opacity: 0, y: 18 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.8, delay: rowNum * 0.12 }}
						className="grid grid-cols-1 gap-6 md:grid-cols-3"
					>
						<div className="md:col-span-1">
							<ArticleCard article={article1} />
						</div>
						{article2 && (
							<div className="md:col-span-2">
								<ArticleCard article={article2} />
							</div>
						)}
					</motion.div>,
				)
			}
		}

		return rowElements
	}

	// Get unique categories for filters
	const categories = [
		...new Set(articles.map((a) => a.category).filter(Boolean)),
	]

	return (
		<section
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<motion.div
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, margin: '-100px' }}
				variants={fadeInUp}
				className="mx-auto max-w-[1600px]"
			>
				{/* Title */}
				{title && (
					<div className="mb-8">
						<h2
							className={cn(HEADING_CLASSES.h2, 'text-black')}
							style={CONDENSED_FONT_STYLE}
						>
							{renderHighlightedTitle(title, content.highlightWords)}
						</h2>
					</div>
				)}

				{/* Filter buttons */}
				{showFilters && categories.length > 0 && (
					<div className="mb-8 flex flex-wrap gap-2">
						<button className="rounded-full bg-[#231a1a] px-4 py-2 text-sm font-medium text-white transition-colors">
							Alle
						</button>
						{categories.map((category) => (
							<button
								key={category}
								className="rounded-full border border-[#231a1a]/20 bg-white px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[#231a1a]/5"
							>
								{category}
							</button>
						))}
					</div>
				)}

				{/* Articles grid with alternating layout */}
				<div className="flex flex-col gap-8">{renderRows()}</div>
			</motion.div>
		</section>
	)
}

// Services List Block - Vertical list with icon image, title, and description per item
function ServicesListBlock({ content }: { content: ServicesListBlockContent }) {
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const items = content.items || []

	if (items.length === 0) return null

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<div className={cn('mx-auto', GRID_NARROW)}>
				{content.title && (
					<motion.h2
						variants={staggerItem}
						className={cn('mb-12', HEADING_CLASSES.h2, 'text-black')}
						style={CONDENSED_FONT_STYLE}
					>
						{renderHighlightedTitle(content.title, content.highlightWords)}
					</motion.h2>
				)}

				<div className="flex flex-col gap-12">
					{items.map((item, index) => (
						<motion.div
							key={index}
							variants={staggerItem}
							className="flex flex-col gap-6 md:flex-row md:gap-10"
						>
							{/* Icon/image - square */}
							{item.image && (
								<div className="h-[120px] w-[120px] shrink-0 overflow-hidden rounded-lg md:h-[178px] md:w-[178px]">
									<Img
										src={getOptimizedImageUrl(item.image) || ''}
										alt={item.title}
										loading="lazy"
										width={178}
										height={178}
										className="h-full w-full object-cover"
									/>
								</div>
							)}

							{/* Text content */}
							<div className="flex flex-1 flex-col gap-4">
								<h3
									className={cn(HEADING_CLASSES.h4, 'text-black')}
									style={CONDENSED_FONT_STYLE}
								>
									{item.title}
								</h3>
								<p className="text-[20px] leading-[1.4] text-black">
									{item.description}
								</p>
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</motion.section>
	)
}

// Content with Sidebar Block - Multiple text sections left, checklist sidebar right
function ContentSidebarBlock({
	content,
}: {
	content: ContentSidebarBlockContent
}) {
	const paddingTop = content.paddingTop ?? 2
	const paddingBottom = content.paddingBottom ?? 2
	const sections = content.sections || []
	const sidebarItems = content.sidebarItems || []

	if (sections.length === 0) return null

	return (
		<motion.section
			initial="initial"
			whileInView="animate"
			viewport={{ once: true, margin: '-100px' }}
			variants={staggerContainer}
			className={cn(
				'bg-[#f7f1e2] px-6 md:px-12 lg:px-24',
				getPaddingClass(paddingTop, 'top'),
				getPaddingClass(paddingBottom, 'bottom'),
			)}
		>
			<div className={cn('mx-auto', GRID_NARROW)}>
				<div className="flex flex-col gap-10 md:flex-row md:gap-10">
					{/* Left column - stacked text sections */}
					<div className="flex flex-[2.5] flex-col gap-10">
						{sections.map((section, index) => (
							<motion.div
								key={index}
								variants={staggerItem}
								className="flex flex-col gap-4"
							>
								<h3
									className={cn(HEADING_CLASSES.h3, 'text-black')}
									style={CONDENSED_FONT_STYLE}
								>
									{section.title}
								</h3>
								<p className="text-[20px] leading-[1.4] text-black">
									{section.description}
								</p>
							</motion.div>
						))}
					</div>

					{/* Right column - sidebar with checklist */}
					{(content.sidebarTitle || sidebarItems.length > 0) && (
						<motion.div
							variants={staggerItem}
							className="flex flex-1 flex-col gap-4"
						>
							{content.sidebarTitle && (
								<h4
									className="text-[24px] leading-none text-black"
									style={CONDENSED_FONT_STYLE}
								>
									{content.sidebarTitle}
								</h4>
							)}

							{sidebarItems.length > 0 && (
								<div className="flex flex-col gap-3">
									{sidebarItems.map((item, index) => (
										<div key={index} className="flex items-center gap-4">
											<Icon
												name="check"
												className="h-4 w-4 shrink-0 text-black"
											/>
											<span className="text-[16px] leading-[1.6] text-black">
												{item.label}
											</span>
										</div>
									))}
								</div>
							)}
						</motion.div>
					)}
				</div>
			</div>
		</motion.section>
	)
}
